import {Injectable, isDevMode, NgZone} from '@angular/core';
import {Events, Loading, LoadingController, Platform, Toast, ToastController} from "ionic-angular";
import {TranslateService} from "@ngx-translate/core";
import {ApiProvider} from "../api/api";
import {GlobalvarsProvider} from "../globalvars/globalvars";
import {LoggerProvider, LogLevel} from "../logger/logger";
import {File} from '@ionic-native/file';
import {FileTransfer, FileTransferObject} from '@ionic-native/file-transfer';
import {Media, MediaObject} from '@ionic-native/media';
import {Storage} from '@ionic/storage';
import {MusicControls} from '@ionic-native/music-controls';
import {FileOpener} from "@ionic-native/file-opener";
import {Deferred} from "ts-deferred";
import moment from 'moment';
import CryptoJS from 'crypto-js';
import * as isNullOrEmpty from 'isnullorempty';
import notDefined from 'not-defined';
import * as Bluebird from 'bluebird';


declare var cordova: any;


interface Channel {
  channel_id: number;
  name: string;
  pic: string;
  tags: Array<string>;
  theme: string;
}

interface PlaylistDate {
  unix: number;
  gmt: string;
}

interface Playlist {
  channel_id: string;
  checksum: string;
  fallback: boolean;
  id: number;
  label: string;
  name: string;
  date: PlaylistDate;
}

interface PlaylistItem {
  artist: string;
  checksum: string;
  file_size: number;
  id: number;
  playtime: number;
  starttime: PlaylistDate;
  title: string;
  track_id: number;
  track_path: string;
  weekday: string;
  fallback: boolean;
}

interface MusicControllerInfo {
  track: string;
  artist: string;
  album: string;
  duration: number;
  elapsed: number;
  ticker: string;
}


@Injectable()
export class SyncProvider {

  private log_level: LogLevel = LogLevel.Error;
  private log_label_main: string = "SyncProvider";

  private loading: Loading;
  private loadingDismussing: boolean = false;

  public autoPlay: boolean = true;
  private autoStartTrackSync: boolean = true; // TODO: integrate in buildscript

  //private maxSyncTry: number = 3;
  private maxDownloadTry: number = 3;
  private downloadTry: number = 0;
  private loopDownloadTry: number = 0;
  private downloadInterval: number = 5; // seconds

  private seekSafetyTime: number = 10;

  public channels: any;
  public channel: Channel;
  public playlists: any = {};
  public fallbacks: any = {};

  public playlist: any;
  public playlist_date: string;

  public playlist_next: any;
  public playlist_date_next: string;

  public playing: boolean = false;
  public current: any;
  public last: any;
  public next: Array<any> = [];

  private fileTransfer: FileTransferObject;

  public storageDirectory: string;
  public storageType: string = 'internal';

  public isSyncingPlaylists: boolean = false;
  public isSyncingPlaylistsStopped: boolean = false;

  public isSyncingTracks: boolean = false;
  public isSyncingTracksStopped: boolean = false;

  private trackDownloadList: any = [];
  private trackKeepList: any = [];

  private trackPlaying: boolean = false;
  private trackingLoop: any;

  private mediaObject: MediaObject;

  public lastSync: moment.Moment = null;
  public lastSyncBegin: moment.Moment = null;
  public nextSync: moment.Moment = null;
  public syncInterval: number = 30; // minutes
  public syncStuckInterval: number = 45; // minutes

  private autoCloseTrackSyncAlertCounter: number = 0;
  private autoCloseTrackSyncAlertCounterMax: number = 10;
  private autoCloseTrackSyncAlertCounterRun: boolean = false;

  public json: string;

  public defectPlaylists: object = {};
  public defectFallbacks: object = {};
  public defectTracks: Array<any> = [];
  // 2019-03-01
  public skipDownloadTracks: Array<any> = [];

  private lastPlaylistUpdate: object = {};

  private interrupt: boolean = false;

  // 2018-10-31
  //private musicControls: any = null;

  // 2018-11-05
  public lastVolume: number = null;

  // 2019-01-06
  private chromecastSession: any;

  // 2019-01-10
  private musicControl: any;
  private musicControlPaused: boolean = false;
  // 2019-01-29
  private musicControlLastTrack: any;

  // SIA-20, 2019-02-03
  //private syncChannel: any = null;
  //private syncChannels: Array<any> = null;

  // 2019-03-14
  public storageSize: number;

  public playlistInfo: any = {};
  public trackInfo: any = {};

  private loopPlaylists: Array<any> = [];
  private loopTracks: Array<any> = [];

  private minDates: any = {};
  private maxDates: any = {};

  private uniqueTracks: any = {};

  private firstPlay: boolean = false;

  private toast: Toast;
  private useToast: boolean = true;
  private pausedToast: boolean = false;
  private dismissedToast: boolean = false;
  private toastMessages: Array<string> = [];
  private toastTimeout: number = 30 * 60 * 1000; // minutes as ms
  private toastLastStart: moment.Moment = null;

  private syncPause: string = null;
  private usePauseSync: boolean = true;

  private cronActive: boolean = false;


  constructor(
    private apiProvider: ApiProvider,
    private translateService: TranslateService,
    //private alertCtrl: AlertController,
    public loadingCtrl: LoadingController,
    public platform: Platform,
    public storage: Storage,
    public zone: NgZone,
    public events: Events,
    private transfer: FileTransfer,
    private file: File,
    private media: Media,
    public globalvarsProvider: GlobalvarsProvider,
    private logger: LoggerProvider,
    private musicControls: MusicControls,
    private fileOpener: FileOpener,
    private toastCtrl: ToastController
  ) {
    const me = this;
    let log_label: string = me.log_label_main + '::constructor';
    me.logger.setLogLevel(me, LogLevel.Warning);

    me.logger.warn(me, log_label);

    me.storageDirectory = me.globalvarsProvider.storageDirectory;

    // event
    me.globalvarsProvider.playoutEvent.subscribe(event => {
      me.logger.info(me, log_label + ':chromecastSessionEvent', {event: event});

      me.chromecastSession = me.globalvarsProvider.getChromeCast();

      me.switchPlayer();
    });

    // 2019-02-21, check play status
    me.events.subscribe('checkPlayerStatus:sfapp', () => {
      me.checkPlayer();
    });

    // 2019-03-19, offline event
    me.events.subscribe('apiStatus:offline', () => {
      me.logger.info(me, log_label + ':apiStatus:offline');

      // 2019-03-23
      // me.stopAutoSyncs();
    });

    // 2019-03-23, global cron
    me.events.subscribe('cron:run', (background: boolean) => {
      me.runCron(background);
    });
  }

  appReady(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::appReady';

    me.logger.warn(me, log_label);

    // promises
    let promises: Array<any> = [];

    return new Promise((resolve, reject) => {
      // 2018-11-07
      promises.push(me.loadVolume());

      // 2019-03-19
      promises.push(me.loadTranslations());

      // init vars
      promises.push(me.initVars());

      Promise.all(promises).then(() => {
        me.logger.warn(me, log_label + ':resolve');

        me.logger.setLogLevel(me, me.log_level);

        resolve();

      }, error => {
        me.logger.error(me, log_label + ':error', {error: error});

        me.logger.setLogLevel(me, me.log_level);

        reject(error);
      });
    });
  }

  private loadTranslations(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::loadTranslations';

    me.logger.warn(me, log_label);

    return new Promise((resolve, reject) => {

      me.translateService.get([
        'toast_sync_start',
        'toast_sync_prepare_playlists',
        'toast_verify_playlist',
        'toast_verify_playlists',
        'toast_sync_download_playlist',
        'toast_sync_download_playlists',
        'toast_sync_prepare_tracks',
        'toast_verify_track',
        'toast_verify_tracks',
        'toast_sync_download_track',
        'toast_sync_download_tracks',
        'toast_sync_finish',
        'toast_sync_cancel',
        'toast_cleanup_tracks',
      ]).subscribe(
        value => {
          me.logger.warn(me, log_label, {translation: value});

          me.toastMessages = value;

          resolve();

        },
        error => {
          reject(error);
        }
      );

    });
  }

  private initVars(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::initVars';

    me.logger.warn(me, log_label);

    return new Promise(resolve => {
      // autoplay
      if (me.platform.is('mobile')) {
        me.autoPlay = false;
      }
      me.logger.info(me, log_label, {autoplay: me.autoPlay});

      // autosync
      if (!isDevMode()) {
        me.autoStartTrackSync = true;
      }
      me.logger.info(me, log_label, {autosync: me.autoStartTrackSync});

      // filetransfer
      me.fileTransfer = me.transfer.create();

      // define storage
      me.logger.debug(me, log_label, {platform: me.platform});
      if (me.platform.is('ios')) {
        me.storageDirectory = me.file.documentsDirectory;
        me.logger.info(me, log_label + ':iOS', {storageDirectory: me.storageDirectory});

      } else if (me.platform.is('android')) {
        if (me.file.externalDataDirectory) {
          me.storageDirectory = me.file.externalDataDirectory;
          me.storageType = 'external';
          me.logger.info(me, log_label + ':Android:external', {storageDirectory: me.storageDirectory});

        } else {
          me.storageDirectory = me.file.dataDirectory;
          me.logger.info(me, log_label + ':Android:internal', {storageDirectory: me.storageDirectory});
        }

      } else if (me.platform.is('core') || me.platform.is('mobileweb')) {
        window.addEventListener('filePluginIsReady', () => {
          me.logger.info(me, log_label + ':filePluginIsReady');
        }, false);

        me.file.getFreeDiskSpace().then(size => {
          me.logger.debug(me, log_label + ':getFreeDiskSpace', {size: size});

        }, error => {
          me.logger.error(me, log_label + ':getFreeDiskSpace', {error: error});
        });

        //window.initPersistentFileSystem(1024 * 1024 * 1024);
      }

      me.logger.info(me, log_label + ':resolve');

      resolve();
    });
  }

  runCron(background: boolean): void {
    const me = this;
    let log_label = me.log_label_main + '::runCron';

    if (me.cronActive) {
      me.logger.warn(me, log_label, {
        background: background
      });

      // trackPlaying
      if (me.current) {
        me.logger.warn(me, log_label + ':playing', {position: me.current.played, trackPlaying: me.trackPlaying});
      }

      // status info
      me.logger.warn(me, log_label + ':playlistInfo', {playlistInfo: me.playlistInfo});
      me.logger.warn(me, log_label + ':trackInfo', {trackInfo: me.trackInfo});

      // checkStuckSync
      me.checkStuckAutoSync();

      // checkPlayer
      if (background) {
        me.checkPlayer();
      }
    }
  }

  stopCron(): void {
    const me = this;
    let log_label = me.log_label_main + '::stopCron';

    me.logger.warn(me, log_label, {cronActive: me.cronActive});

    me.cronActive = false;
  }

  startCron(): void {
    const me = this;
    let log_label = me.log_label_main + '::startCron';

    me.logger.warn(me, log_label, {cronActive: me.cronActive});

    me.cronActive = true;
  }

  getChannels(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getChannels';

    me.logger.warn(me, log_label);

    return new Promise(function(resolve, reject) {
      // translate
      me.translateService.get('loading_text_loadchannels').subscribe(
        value => {
          // loading screen
          me.showLoading(value);
        }
      );

      // check storage for channels
      let storageLabel: string = 'channels';
      me.storage.get(storageLabel).then(channels => {
        me.logger.info(me, log_label + "storage:get", {storageLabel: storageLabel, channels: channels});

        if (!isNullOrEmpty.isNullOrEmpty(channels)) {
          me.channels = channels;

          me.dismissLoading().then(() => {
            resolve(me.channels);
          });

        } else {
          me.logger.warn(me, log_label + "storage:get:empty", {storageLabel: storageLabel});

          me.loadChannels().then(data => {
            me.channels = data.channels;
            resolve(me.channels);

          }, error => {
            reject(error);
          });
        }

      }, error => {
        me.logger.error(me, log_label + "storage:get:error", {storageLabel: storageLabel, error: error});

        me.loadChannels().then(data => {
          me.channels = data.channels;
          resolve(me.channels);

        }, error => {
          reject(error);
        });
      });
    });
  }

  private loadChannels(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::loadChannels';

    me.logger.warn(me, log_label);

    return new Promise(function(resolve, reject) {
      // api call
      let apiLabel: string = 'callGetChannels';
      me.apiProvider.callGetChannels(me.loading, true).then(data => {
        me.logger.info(me, log_label + "api:call", {apiLabel: apiLabel, data: data});
        resolve(data);

      }, error => {
        me.logger.error(me, log_label + "api:call:error", {apiLabel: apiLabel, error: error});
        reject(error);
      });
    });
  }

  private checkChannelChange(): void {
    const me = this;
    let log_label = me.log_label_main + '::checkChannelChange';

    me.logger.warn(me, log_label);
  }

  getPlaylists(channel: any, set: boolean = false): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getPlaylists';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      channel: channel,
      set: set
    });

    if (set) {
      me.channel = channel;
    }

    return new Promise(function(resolve, reject) {
      // translate
      me.translateService.get('loading_text_getplaylists').subscribe(
        value => {
          // loading screen
          me.showLoading(value);
        }
      );

      // check storage for playlists and fallbacks by channel
      let promises: Array<any> = [];

      let playlists_label = "playlists_"+channel.channel_id;
      promises.push(me.storage.get(playlists_label));

      let fallbacks_label = "fallbacks_"+channel.channel_id;
      promises.push(me.storage.get(fallbacks_label));

      Promise.all(promises).then((data) => {
        let playlists = data[0];
        me.logger.info(me, log_label + ":" + playlists_label, {playlists: playlists});

        let fallbacks = data[1];
        me.logger.info(me, log_label + ":" + fallbacks_label, {fallbacks: fallbacks});

        if (
          !isNullOrEmpty.isNullOrEmpty(playlists)
          &&
          !isNullOrEmpty.isNullOrEmpty(fallbacks)
        ) {
          // all ready
          me.getPlaylistsResult(playlists, fallbacks, channel.channel_id).then(() => resolve(), error => reject(error));

        } else {
          me.logger.info(me, log_label + ":" + playlists_label + ":nullOrEmpty");
          me.logger.info(me, log_label + ":" + fallbacks_label + ":nullOrEmpty");

          me.getPlaylistsError(channel).then(() => resolve(), error => reject(error));
        }

      }, error => {
        me.logger.info(me, log_label + ":" + playlists_label + ":notFound");
        me.logger.info(me, log_label + ":" + fallbacks_label + ":notFound");

        me.logger.error(me, log_label, {error: error});

        me.getPlaylistsError(channel).then(() => resolve(), error => reject(error));
      });
    });
  }

  getPlaylistsError(channel: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getPlaylistsError';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      channel: channel
    });

    return new Promise(function(resolve, reject) {
      me.loadPlaylists(channel).then(data => {
        me.logger.info(me, log_label + ":loadPlaylists:data", {keys: Object.keys(data)});
        me.logger.debug(me, log_label + ":loadPlaylists:data", {data: data});

        if (data.data && data.fallback) {
          me.getPlaylistsResult(data.data, data.fallback, data.meta.channel.id).then(() => resolve(), error => reject(error));

        } else {
          me.getPlaylistsResult(data.playlists, data.fallbacks, data.meta.channel.id).then(() => resolve(), error => reject(error));
        }

      }, error => {
        me.dismissLoading().then(() => reject(error));
      });
    });
  }

  getPlaylistsResult(playlists: any, fallbacks: any, channel_id: number, hideLoading: boolean = true): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getPlaylistsResult';

    me.logger.warn(me, log_label, {channel_id: channel_id});
    me.logger.info(me, log_label, {
      playlists: playlists,
      fallbacks: fallbacks,
      channel_id: channel_id
    });

    return new Promise(function(resolve) {
      me.playlists[channel_id] = playlists;
      me.fallbacks[channel_id] = fallbacks;

      if (hideLoading) {
        me.dismissLoading()
          .then(() => resolve())
          .catch(() => resolve());

      } else {
        resolve();
      }
    });
  }

  loadPlaylists(channel: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::loadPlaylists';

    me.logger.warn(me, log_label);

    return new Promise(function(resolve, reject) {
      me.apiProvider.callGetPlaylists(me.loading, channel, true, true).then(data => {
        me.logger.info(me, log_label + ':loaded', {
          keys: Object.keys(data),
          data: data
        });

        resolve(data);

      }, error => {
        me.logger.info(me, log_label + ':error', {error: error});

        reject(error);
      });
    });
  }

  stopTrackSync(): void {
    const me = this;
    let log_label = me.log_label_main + '::stopTrackSync';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {channel: me.channel});

    me.isSyncingTracksStopped = true;
    me.isSyncingTracks = false;

    me.cancelSyncToast();
  }

  getFileName(track: any, withoutTrackId: boolean = false): string {
    let filename: string = track.track_path.substring(track.track_path.lastIndexOf('/') + 1, (track.track_path.length));

    if (withoutTrackId) {
      return filename;

    } else {
      return track.track_id + "_" + filename;
    }
  }

  getTracksLabel(channel: any = null): string {
    if (isNullOrEmpty.isNullOrEmpty(channel)) {
      // default
      return "tracks_" + this.channel.channel_id;

    } else {
      // provided channel
      return "tracks_" + channel.channel_id;
    }
  }

  getPlaylistsLabel(channel: any = null): string {
    const label = "playlists_";

    if (isNullOrEmpty.isNullOrEmpty(channel)) {
      // default
      return label + this.channel.channel_id;

    } else {
      // provided channel
      if (typeof channel == "string") {
        return label + channel;

      } else {
        return label + channel.channel_id;
      }
    }
  }

  getFallbacksLabel(channel: any = null): string {
    const label = "fallbacks_";

    if (isNullOrEmpty.isNullOrEmpty(channel)) {
      // default
      return label + this.channel.channel_id;

    } else {
      // provided channel
      if (typeof channel == "string") {
        return label + channel;

      } else {
        return label + channel.channel_id;
      }
    }
  }

  getShortChannelName(channel: any = null): string {
    const me = this;
    let log_label = me.log_label_main + '::getShortChannelName';

    me.logger.info(me, log_label, {name: channel.name});

    if (channel.name.includes("(")) {
      // "(" detected
      return channel.name.split("(")[0];

    } else if (channel.name.includes("/")) {
      // "/" detected
      return channel.name.split("/")[0];

    } else if (channel.name.includes("-")) {
      // "-" detected
      return channel.name.split("-")[0];

    } else {
      // no processing
      return channel.name;
    }
  }

/*
  startTrackSync(force: boolean = false, channel: any = null, sync: boolean = true): void {
    const me = this;
    let log_label = me.log_label_main + '::startTrackSync';

    me.logger.warn(me, log_label);

    let currentChannel: any = me.getCurrentChannel(channel);

    me.logger.warn(me, log_label, {
      channel_id: currentChannel.channel_id,
      sync: sync,
      force: force
    });

    me.logger.info(me, log_label, {
      channel: channel,
      currentChannel: currentChannel,
      force: force,
      sync: sync,
      isSyncingTracksStopped: me.isSyncingTracksStopped
    });

    if (force && sync) {
      me.isSyncingTracksStopped = false;
    }

    if (sync) me.isSyncingTracks = true;
    me.trackDownloadList = [];
    me.trackKeepList = [];

    let unique_tracks: Array<any> = [];
    let unique_track_names: Array<any> = [];

    let tracks_label: string = me.getTracksLabel(currentChannel);
    let playlists_label: string = me.getPlaylistsLabel(currentChannel);
    let fallbacks_label: string = me.getFallbacksLabel(currentChannel);

    me.logger.info(me, log_label, {
      tracks_label: tracks_label,
      playlists_label: playlists_label,
      fallbacks_label: fallbacks_label
    });

    let promises: Array<any> = [];
    let count: number = 0;
    let tracks_length: number = 0;

    // tracks
    promises.push(me.getStoredTracks(tracks_label).then(data => {
      let tracks: Array<any> = data.tracks;
      tracks_length = data.tracks_length;

      // playlist loop
      promises.push(me.catalogizePlaylists(count, playlists_label, tracks, unique_tracks, unique_track_names).then(() => {

      }, error => {
        me.logger.error(me, log_label + ":error", {playlists_label: playlists_label, error: error});

        // 2019-02-12, TODO: sync check?
        if (sync) me.isSyncingTracks = false;
      }));

      // fallback loop
      promises.push(me.catalogizePlaylists(count, fallbacks_label, tracks, unique_tracks, unique_track_names).then(() => {

      }, error => {
        me.logger.error(me, log_label + ":error", {fallbacks_label: fallbacks_label, error: error});

        // 2019-02-12, TODO: sync check?
        if (sync) me.isSyncingTracks = false;
      }));

      // all completed
      me.logger.warn(me, log_label + ':promises', {promisesLength: promises.length});
      me.completeTrackSync(promises, channel, sync);

    }, error => {
      me.logger.error(me, log_label + ":error", {
        tracks_label: tracks_label,
        fallbacks_label: fallbacks_label,
        error: error
      });

      if (sync) me.isSyncingTracks = false;
    }));
  }
*/

/*
  private getStoredTracks(tracks_label: string): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getStoredTracks';

    me.logger.warn(me, log_label, {tracks_label: tracks_label});

    let tracks_length: number = 0;

    return new Promise(function(resolve, reject) {
      me.storage.get(tracks_label).then(tracks => {
        if (!isNullOrEmpty.isNullOrEmpty(tracks)) {
          tracks_length = Object.keys(tracks).length
        }

        me.logger.info(me, log_label, {
          tracks_length: tracks_length,
        });

        resolve({
          tracks: tracks,
          tracks_length: tracks_length
        });

      }, error => {
        reject(error);
      });
    });
  }
*/

/*
  private catalogizePlaylists(count: number, pls_label: string, tracks: Array<any>, unique_tracks: Array<any>, unique_track_names: Array<any>): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::catalogizePlaylists';

    me.logger.warn(me, log_label, {pls_label: pls_label});

    let promises: Array<any> = [];

    return new Promise(function(resolve, reject) {

      me.storage.get(pls_label).then(playlists => {
        me.logger.debug(me, log_label, {
          pls_label: pls_label,
          pls_type: typeof playlists
        });

        if (isNullOrEmpty(playlists)) {
          // TODO: empty playlists
          reject('empty playlists ' + pls_label);

        } else {
          me.logger.info(me, log_label, {
            pls_label: pls_label,
            pls_length: playlists.length,
          });
          count += playlists.length;

          playlists.forEach(playlist => {
            let playlist_label = pls_label + "::" + playlist.name;

            me.logger.info(me, log_label, {
              playlist_label: playlist_label,
              count: count,
              promises: promises.length
            });

            promises.push(me.storage.get(playlist_label).then(playlist_tracks => {
              if (isNullOrEmpty.isNullOrEmpty(playlist_tracks) || typeof playlist_tracks != "object") {
                // TODO: empty playlist
                reject('empty playlist ' + playlist_label);

              } else {
                playlist_tracks.forEach(playlist_track => {
                  me.logger.debug(me, log_label, {
                    playlist_track: playlist_track
                  });

                  let add: boolean = false;
                  let keep: boolean = false;

                  if (isNullOrEmpty.isNullOrEmpty(tracks)) {
                    add = true;
                    keep = true;

                  } else {
                    if (!tracks[playlist_track.track_id]) {
                      add = true;
                      keep = true;

                    } else {
                      keep = true;
                    }
                  }

                  if (add) {
                    // download list
                    if (
                      !unique_tracks.includes(playlist_track.track_id)

                      // 2019-02-27, try defect tracks again
                      /*
                      &&
                      !me.defectTracks.includes(playlist_track.track_id)
                      */
/*
                      // 2019-03-01, but skip failed tracks for this sync
                      &&
                      !me.skipDownloadTracks.includes(playlist_track.track_id)
                    ) {
                      unique_tracks.push(playlist_track.track_id);
                      me.trackDownloadList.push(playlist_track);
                    }
                  }

                  if (keep) {
                    // keep list
                    if (
                      !unique_track_names.includes(playlist_track.track_path)
                      &&
                      !me.defectTracks.includes(playlist_track.track_id)
                    ) {
                      unique_track_names.push(playlist_track.track_path);
                      me.trackKeepList.push(playlist_track.track_path);
                    }
                  }
                });
              }
            }));

            /*count++;
            if (count == 2) {
              me.completeTrackSync(promises, debug, log_label);
            }*/
/*
          });

          Promise.all(promises).then(() => {
            resolve();

          }, error => {
            reject(error);
          });
        }

      }, error => {
        reject(error);
      });

    });
  }
*/

/*
  private completeTrackSync(promises: any, channel: any = null, sync: boolean): void {
    const me = this;
    let log_label = me.log_label_main + '::completeTrackSync';

    me.logger.warn(me, log_label);

    // all check
    Promise.all(promises).then(() => {
      me.logger.warn(me, log_label + ":done", {
        trackDownloadList: me.trackDownloadList,
        trackKeepList: me.trackKeepList,
        defectTracks: me.defectTracks,
        skipDownloadTracks: me.skipDownloadTracks,
        sync: sync
      });

      // 20196-02-12, new promises
      let promises2: Array<any> = [];

      if (sync) {
        let deferred: Deferred<any> = new Deferred<any>();
        promises2.push(deferred.promise);

        me.loopDownloadTry = 0;
        me.loopTrackDownload(channel, deferred);
      }

      // 2019-02-12
      Promise.all(promises2).then(() => {
        me.logger.warn(me, log_label + ":done2", {

        });

        me.loopTrackClearance(channel);

        if (sync) {
          me.startAutoSync(true);
        }
      });

    }, error => {
      me.logger.error(me, log_label + ":error", {
        trackDownloadList: me.trackDownloadList,
        trackKeepList: me.trackKeepList,
        defectTracks: me.defectTracks,
        skipDownloadTracks: me.skipDownloadTracks,
        sync: sync,
        error: error
      });

      // 2019-02-12, TODO: maybe needed?
      if (sync) {
        me.startAutoSync();
      }
    });
  }
*/

/*
  stopAutoSyncTimeout(): void {
    const me = this;
    let log_label = me.log_label_main + '::stopAutoSync';

    me.logger.warn(me, log_label);
    me.logger.debug(me, log_label, {
      sync_timeout: me.sync_timeout
    });

    if (me.sync_timeout) {
      me.logger.warn(me, log_label + ':clearTimeout', {sync_timeout: me.sync_timeout});

      clearTimeout(me.sync_timeout);
    }
  }
*/

  resetSyncTimes(): void {
    const me = this;
    let log_label = me.log_label_main + '::resetSyncTimes';

    me.logger.warn(me, log_label);

    me.lastSync = null;
    me.nextSync = null;
  }

  resetDefectTracks(): void {
    const me = this;
    let log_label = me.log_label_main + '::resetDefectTracks';

    me.logger.warn(me, log_label);

    me.defectTracks = [];
    me.skipDownloadTracks = [];
  }

  presentToast(message: string): void {
    const me = this;
    let log_label = me.log_label_main + '::presentToast';

    me.logger.info(me, log_label, {useToast: me.useToast, pausedToast: me.pausedToast});

    if (
      me.useToast
      &&
      !me.pausedToast
    ) {

      if (me.toast) {
        me.toast.setMessage(message);
        //me.toast.setDuration(me.toastTimeout);

      } else {

        me.toast = me.toastCtrl.create({
          message: message,
          //duration: me.toastTimeout,
          showCloseButton: true,
          closeButtonText: 'Ok',
          position: 'top'/*,
          cssClass: 'sync-notification'*/
        });

        me.toast.onDidDismiss(() => {
          me.toast = null;

          let now: moment.Moment = moment();
          let dismiss: moment.Moment = me.toastLastStart.clone().add(me.toastTimeout, 'milliseconds');

          me.logger.warn(me, log_label + ':onDidDismiss', {
            dismissedToast: me.dismissedToast,
            toastLastStart: me.logger.formatMoment(me.toastLastStart),
            dismiss: me.logger.formatMoment(dismiss),
            now: me.logger.formatMoment(now)
          });

          if (
            !me.dismissedToast
            //||
            //dismiss < now
          ) {
            me.pausedToast = true;
          }
        });

        me.toast.present()
          .then(() => {
            me.dismissedToast = false;
          })
          .catch(() => {})
        ;
      }
    }
  }

  resetToast(): void {
    const me = this;
    let log_label = me.log_label_main + '::resetToast';

    me.logger.warn(me, log_label, {useToast: me.useToast, pausedToast: me.pausedToast});

    me.pausedToast = false;
    me.dismissedToast = false;

    me.toastLastStart = moment();
  }

  dismissToast(instant: boolean = false): void {
    const me = this;
    let log_label = me.log_label_main + '::dismissToast';

    me.logger.warn(me, log_label, {instant: instant});

    if (me.toast) {
      let timeout: number = 3000;
      if (instant) {
        timeout = 1;
      }

      setTimeout(() => {
        if (me.toast) {
          me.dismissedToast = true;
          me.toast.dismiss().then(() => {}).catch(() => {});
        }
      }, timeout);
    }
  }

  cancelSyncToast(): void {
    const me = this;
    let log_label = me.log_label_main + '::cancelSyncToast';

    me.logger.warn(me, log_label);

    me.presentToast(me.toastMessages['toast_sync_cancel']);

    me.dismissToast();
  }

  startAutoSync(): void {
    const me = this;
    let log_label = me.log_label_main + '::startAutoSync';

    me.logger.warn(me, log_label);

    // get free space and channels
    let promises: Array<any> = [];

    // 2019-03-23
    me.lastSync = moment();
    me.lastSyncBegin = moment();

    // 2019-03-19, toast
    me.resetToast();
    me.presentToast(me.toastMessages['toast_sync_start']);

    promises.push(me.getFreeSpace());
    promises.push(me.loadChannels());

    Promise.all(promises).then(() => {
      me.logger.warn(me, log_label + ':promises:done');

      me.checkChannelChange();
      me.preparePlaylistLoop();

    }, error => {
      me.logger.error(me, log_label + ':promises:error', {error: error});

      me.cancelSyncToast();
    })

/*
    me.logger.warn(me, log_label, {
      done: done,
      instant: instant,
      sync_interval: me.sync_interval
    });
    me.logger.warn(me, log_label, {
      syncChannel: me.syncChannel
    });
    me.logger.warn(me, log_label, {
      syncChannels: me.syncChannels,
      syncChannelsLength: (me.syncChannels) ? me.syncChannels.length : 0
    });
    me.logger.warn(me, log_label + ':checks', {
      done: done,
      syncChannelisNull: !isNullOrEmpty.isNull(me.syncChannel),
      syncChannelisNullOrEmpty: !isNullOrEmpty.isNullOrEmpty(me.syncChannel),
      syncChannelsisNull: !isNullOrEmpty.isNull(me.syncChannels),
      syncChannelsisNullOrEmpty: !isNullOrEmpty.isNullOrEmpty(me.syncChannels),
      syncChannelsisEmpty: !isNullOrEmpty.isEmpty(me.syncChannels)
    });
    me.logger.warn(me, log_label + ':checks_sync', {
      isSyncingPlaylists: me.isSyncingPlaylists,
      isSyncingTracks: me.isSyncingTracks
    });

    // 2019-03-01, reset list
    if (!done && instant) {
      me.logger.warn(me, log_label + ':resetSkipDownloadTracks');

      me.skipDownloadTracks = [];
    }

    // SIA-20, 2019-02-03, added multi channel sync
    if (me.isSyncingPlaylists || me.isSyncingTracks ) {
      // check stuck sync
      me.runAutoSync(done);

      return;

    } else if (
      // 2019-02-12, new
      isNullOrEmpty.isNull(me.syncChannel)
      &&
      isNullOrEmpty.isNull(me.syncChannels)
      &&
      !done
    ) {
        me.startAutoSyncNew();

        return;

    // 2019-02-12, continue
    } else if (
      !isNullOrEmpty.isNull(me.syncChannel)
      &&
      !isNullOrEmpty.isNullOrEmpty(me.syncChannels)
      &&
      done
    ) {
      me.startAutoSyncContinue();

      return;

    // 2019-02-12, stop => prepare cron
    } else {
      me.resetSyncVars();

      me.runAutoSync(done);

      return;
    }
/*
    } else {
      // continue
      me.logger.warn(me, log_label + ':continue');
      me.logger.warn(me, log_label + ':continue', {
        done: done,
        syncChannelCheck: !isNullOrEmpty.isNull(me.syncChannel),
        syncChannelsCheck: !isNullOrEmpty.isNullOrEmpty(me.syncChannels)
      });

      if (done) {
        // 2019-02-12
        if (!isNullOrEmpty.isNull(me.syncChannel)) {

          if (!isNullOrEmpty.isNullOrEmpty(me.syncChannels)) {
            // next
            me.logger.warn(me, log_label + ':continue:next');

            syncChannel = me.syncChannels.pop();
            me.syncChannel = syncChannel;

            instant = true;

          } else {
            // last
            me.logger.warn(me, log_label + ':continue:last');

            syncChannel = {...me.syncChannel};
            me.syncChannel = null;

            instant = true;

          }

        } else {
          // stop
          me.logger.warn(me, log_label + ':continue:stop');

          me.syncChannel = null;
          me.syncChannels = null;
        }

        /*
        if (me.syncChannels && me.syncChannels.length > 0) {
          // next
          me.logger.warn(me, log_label + ':continue:next');

          syncChannel = me.syncChannels.pop();
          me.syncChannel = syncChannel;

          instant = true;

        } else {
          // stop
          me.logger.warn(me, log_label + ':continue:stop');

          me.syncChannel = null;
          me.syncChannels = null;
        }
        */
/*
        me.logger.warn(me, log_label + ':continue:status', {
          syncChannelLoc: syncChannel
        });
        me.logger.warn(me, log_label + ':continue:status', {
          syncChannel: me.syncChannel
        });
        me.logger.warn(me, log_label + ':continue:status', {
          syncChannels: me.syncChannels,
          syncChannelsLength: (me.syncChannels) ? me.syncChannels.length : 0
        });
      }
    }

    me.logger.warn(me, log_label + ':sync', {
      done: done,
      instant: instant,
      sync_interval: me.sync_interval
    });
    me.logger.warn(me, log_label + ':sync', {
      syncChannelLoc: syncChannel
    });
    me.logger.warn(me, log_label + ':sync', {
      syncChannel: me.syncChannel
    });
    me.logger.warn(me, log_label + ':sync', {
      syncChannels: me.syncChannels,
      syncChannelsLength: (me.syncChannels) ? me.syncChannels.length : 0
    });

    let now: any = moment();
    let next: any = moment().add(me.sync_interval, "minutes");

    let diff = next.diff(now, "milliseconds");
    // SIA-20, 2019-02-03, manual instant added
    if (instant) {
      diff = 1000;
    }
    me.logger.warn(me, log_label + ':diff', {diff: diff});

    if (done) {
      me.lastSync = now;
    }
    me.next_sync = next;

    // 2019-01-11
    me.stopAutoSyncTimeout();

    me.sync_timeout = setTimeout(() => {
      me.logger.warn(me, log_label + ':sync_timeout');
      me.logger.info(me, log_label + ':sync_timeout', {
        isSyncingPlaylists: me.isSyncingPlaylists,
        isSyncingTracks: me.isSyncingTracks,
        instant: instant
      });

      if (!me.isSyncingPlaylists && !me.isSyncingTracks ) {
        me.logger.warn(me, log_label + ':sync_timeout:run');

        // SIA-27, 2019-02-03
        me.clearMusicDirectory().then(() => {
          me.logger.warn(me, log_label + ':sync_timeout:run:cleared');

          // rerun sync
          me.lastSync = now;

          // SIA-20, 2019-02-03
          //me.startPlaylistSync(true, syncChannel);
          // SIA-28, 2019-02-05
          if (instant) {
            me.logger.warn(me, log_label + ':sync_timeout:run:instant', {
              syncChannelLoc: syncChannel
            });
            me.logger.warn(me, log_label + ':sync_timeout:run:instant', {
              syncChannel: me.syncChannel
            });
            me.logger.warn(me, log_label + ':sync_timeout:run:instant', {
              syncChannels: me.syncChannels,
              syncChannelsLength: (me.syncChannels) ? me.syncChannels.length : 0
            });

            me.startPlaylistSync(true, syncChannel);

          } else {
            me.logger.warn(me, log_label + ':sync_timeout:run:prepare');

            me.startAutoSync(false, true);
          }

        }, error => {
          me.logger.error(me, log_label + ':sync_timeout:run:cleared:error', {error: error});

        });

      } else {
        // sync is still running
        me.checkStuckAutoSync();

        me.startAutoSync();
      }

    }, diff);

    me.logger.warn(me, log_label + ':sync_timeout:set', {sync_timeout: me.sync_timeout});
    */
  }

/*
  resetSyncVars(): void {
    const me = this;
    let log_label = me.log_label_main + '::resetSyncVars';

    me.logger.warn(me, log_label);

    me.syncChannel = null;
    me.syncChannels = null;
  }
*/

  completeAutoSync(): void {
    const me = this;
    let log_label = me.log_label_main + '::completeAutoSync';

    me.logger.warn(me, log_label);

    let next: moment.Moment = me.lastSyncBegin.clone().add(me.syncInterval, "minutes");
    me.nextSync = next;

    // 2019-01-11
    // 2019-03-23
    //me.stopAutoSyncTimeout();

    // 2019-03-19, toast
    me.presentToast(me.toastMessages['toast_sync_finish']);
    me.dismissToast();

    /*
    me.sync_timeout = setTimeout(() => {
      me.logger.warn(me, log_label + ':sync_timeout');

      me.checkStuckAutoSync();

      me.startAutoSync(false, true);

    }, diff);

    me.logger.warn(me, log_label + ':sync_timeout:set', {sync_timeout: me.sync_timeout, diff: diff});

    // 2019-03-22
    me.resumeSync();
    */
  }

/*
  startAutoSyncNew(): void {
    const me = this;
    let log_label = me.log_label_main + '::startAutoSyncNew';

    me.logger.warn(me, log_label);

    let syncChannel: any;

    // new run
    me.logger.warn(me, log_label + ':new-run', {
      channelsLength: (me.channels) ? Object.keys(me.channels).length : 0
    });

    syncChannel = me.getCurrentChannel();
    me.syncChannel = syncChannel;
    me.syncChannels = [];

    me.channels.forEach((ch) => {
      me.logger.debug(me, log_label + ':new-run:foreach', {
        ch: ch,
        syncChannel: me.syncChannel
      });

      if (ch.channel_id != syncChannel.channel_id) {
        me.syncChannels.push(ch);
      }
    });

    me.logger.warn(me, log_label + ':new-run:status', {
      syncChannel: me.syncChannel
    });
    me.logger.warn(me, log_label + ':new-run:status', {
      syncChannels: me.syncChannels,
      syncChannelsLength: (me.syncChannels) ? me.syncChannels.length : 0
    });

    me.startAutoSyncInstant(syncChannel);
  }

  startAutoSyncContinue(): void {
    const me = this;
    let log_label = me.log_label_main + '::startAutoSyncContinue';

    me.logger.warn(me, log_label);

    let syncChannel: any;

    // next
    //syncChannel = me.syncChannels.pop(); // last
    syncChannel = me.syncChannels.shift(); // first
    me.syncChannel = syncChannel;

    me.startAutoSyncInstant(syncChannel);
  }

  startAutoSyncInstant(syncChannel: any): boolean {
    const me = this;
    let log_label = me.log_label_main + '::startAutoSyncInstant';

    me.logger.warn(me, log_label);

    // 2019-01-11
    //me.stopAutoSyncTimeout();

    // 2019-02-12
    //me.stopAutoSyncTimeout();
    //me.stopAutoSyncs();

    let now: any = moment();

    if (!me.isSyncingPlaylists && !me.isSyncingTracks ) {
      me.logger.warn(me, log_label + ':run');

      // SIA-27, 2019-02-03
      me.clearMusicDirectory().then(() => {
        me.logger.warn(me, log_label + ':run:cleared');

        // rerun sync
        me.lastSync = now;

        me.logger.warn(me, log_label + ':run:instant', {
          syncChannelLoc: syncChannel
        });
        me.logger.warn(me, log_label + ':run:instant', {
          syncChannel: me.syncChannel
        });
        me.logger.warn(me, log_label + ':run:instant', {
          syncChannels: me.syncChannels,
          syncChannelsLength: (me.syncChannels) ? me.syncChannels.length : 0
        });

        me.startPlaylistSync(true, syncChannel);

      }, error => {
        me.logger.error(me, log_label + ':run:cleared:error', {error: error});

      });

      return true;

    } else {
      me.logger.warn(me, log_label + ':running');

      return false;
    }
  }
*/

  checkStuckAutoSync(): void {
    const me = this;
    let log_label = me.log_label_main + '::checkStuckAutoSync';

    let now: moment.Moment = moment();
    let stuck: moment.Moment = moment().subtract(me.syncStuckInterval, "minutes");

    me.logger.warn(me, log_label);
    me.logger.warn(me, log_label, {
      lastSync: me.logger.formatMoment(me.lastSync),
      nextSync: me.logger.formatMoment(me.nextSync),
      stuck: me.logger.formatMoment(stuck),
      syncStuckInterval: me.syncStuckInterval,
      firstPlay: me.firstPlay
    });

    if (!me.lastSync) {
      // not yet run

      if (me.firstPlay) {
        // start first sync after first playing
        me.logger.warn(me, log_label + ':firstPlay:firstSync');

        me.startAutoSync();

      } else {
        // nothing to do
        me.logger.warn(me, log_label + ':firstPlay:nothingToDo');
      }

    } else {

      if (
        me.nextSync
        &&
        me.nextSync < now
        &&
        me.nextSync > me.lastSync
        &&
        me.firstPlay
      ) {
        // normal sync
        me.logger.warn(me, log_label + ':sync:normal');

        me.lastSync = null;

        me.resetFirstPlay();

      } else if (me.lastSync < stuck) {
        // stuck
        me.logger.warn(me, log_label + ':sync:stuck');

        me.stopAutoSyncs();

        me.lastSync = null;

        me.resetFirstPlay();

      } else {
        // nothing to do
        me.logger.warn(me, log_label + ':sync:nothingToDo');
      }

    }
  }

  stopAutoSyncs(): void {
    const me = this;
    let log_label = me.log_label_main + '::stopAutoSyncs';

    me.logger.warn(me, log_label);
    me.logger.warn(me, log_label, {
      isSyncingPlaylists: me.isSyncingPlaylists,
      isSyncingTracks: me.isSyncingTracks
    });

    if (me.isSyncingPlaylists) {
      me.stopPlaylistSync();
    }

    if (me.isSyncingTracks) {
      me.stopTrackSync();
    }
  }

  preparePauseSync(): void {
    const me = this;
    let log_label = me.log_label_main + '::preparePauseSync';

    me.logger.warn(me, log_label);

    let isSyncingPlaylists: boolean = me.isSyncingPlaylists;
    let isSyncingTracks: boolean = me.isSyncingTracks;

    if (isSyncingPlaylists || isSyncingTracks) {
      //me.stopAutoSyncs();

      if (isSyncingPlaylists && !me.isSyncingPlaylists) {
        me.syncPause = "playlists";
        //me.dismissToast();

      } else if (isSyncingTracks && !me.isSyncingTracks) {
        me.syncPause = "tracks";
        //me.dismissToast();

      } else {
        me.unpauseSync();
      }
    }
  }

  pauseSync(): void {
    const me = this;
    let log_label = me.log_label_main + '::pauseSync';

    me.logger.warn(me, log_label, {syncPause: me.syncPause});

    if (!notDefined(me.syncPause)) {
      me.stopAutoSyncs();
    }
  }

  unpauseSync(): void {
    const me = this;
    let log_label = me.log_label_main + '::unpauseSync';

    me.logger.warn(me, log_label);

    me.syncPause = null;
  }

  resumeSync(): void {
    const me = this;
    let log_label = me.log_label_main + '::resumeSync';

    me.logger.warn(me, log_label, {syncPause: me.syncPause});

    if (me.syncPause == "playlists") {
      me.preparePlaylistLoop();

    } else if (me.syncPause == "tracks") {
      me.prepareTrackLoop();

    }

    me.syncPause = null;
  }

  clearMusicDirectory(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::clearMusicDirectory';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      storageDirectory: me.storageDirectory,
      channels: me.channels
    });

    return new Promise(function(resolve, reject) {

      let keep: Array<string> = [];
      me.channels.forEach((channel) => {
        let ch_dir: string = "ch_" + channel.channel_id;
        keep.push(ch_dir);

        let playlist: string =  "playlists_" + channel.channel_id + ".xslx";
        keep.push(playlist);
      });

      me.logger.warn(me, log_label + ':keep', {
        keep: keep
      });

      me.listDir('').then(listDir => {
        me.logger.info(me, log_label + ':dir:listDir', {listDir: listDir});

        let deletes: Array<any> = [];

        listDir.forEach((item) => {
          if (!keep.includes(item.name)) {
            deletes.push(item);
          }
        });
        me.logger.info(me, log_label + ':dir:listDir:delete', {deletes: deletes});

        let promises: Array<any> = [];
        deletes.forEach((item) => {
          if (item.isDirectory) {
            promises.push(me.file.removeRecursively(me.storageDirectory, item.name));

          } else if (item.isFile) {
            promises.push(me.file.removeFile(me.storageDirectory, item.name));
          }
        });

        Promise.all(promises).then(() => {
          me.logger.warn(me, log_label + ':dir:listDir:done');

          resolve();

        }, (error) => {
          me.logger.error(me, log_label + ':dir:listDir:done:error', {error: error});

          reject(error);

        });

      }, error => {
        me.logger.error(me, log_label + ':dir:listDir:error', {error: error});

        reject(error);

      });

    });
  }

  decreaseStorageSize(size: number): void {
    const me = this;
    let log_label = me.log_label_main + '::decreaseStorageSize';

    me.logger.info(me, log_label, {size: size, storageSize: me.storageSize});

    if (me.storageSize > 0) {
      me.storageSize -= size / 1024 / 1024;
      if (me.storageSize < 0) {
        me.storageSize = 0;
      }

      me.triggerUpdateEventStorageSize();
    }
  }

  triggerUpdateEventStorageSize(): void {
    const me = this;
    let log_label = me.log_label_main + '::triggerUpdateEventStorageSize';

    me.logger.info(me, log_label);

    me.events.publish('syncService:update', {type: 'storageSize', data: me.storageSize});
  }

  triggerUpdateEventPlaylist(): void {
    const me = this;
    let log_label = me.log_label_main + '::triggerUpdateEventPlaylist';

    me.logger.info(me, log_label);

    me.events.publish('syncService:update', {type: 'playlists', data: me.playlistInfo});
  }

  triggerUpdateEventTrack(): void {
    const me = this;
    let log_label = me.log_label_main + '::triggerUpdateEventTrack';

    me.logger.info(me, log_label);

    me.events.publish('syncService:update', {type: 'tracks', data: me.trackInfo});
  }

  loopTrackDownload(): void {
    const me = this;
    let log_label = me.log_label_main + '::loopTrackDownload';

    me.logger.warn(me, log_label, me.loopTracks.length);
    me.logger.info(me, log_label, {
      isSyncingTracksStopped: me.isSyncingTracksStopped,
    });
    me.logger.debug(me, log_label, {
      loopTracks: me.loopTracks
    });

    // check stop
    if (me.isSyncingTracksStopped) {
      me.logger.warn(me, log_label + ':stopped');

      me.completeAutoSync();

      return;
    }

    // check finish
    if (me.loopTracks.length == 0) {
      // finish
      me.logger.warn(me, log_label + ':finish', {playlistInfo: me.playlistInfo});

      me.isSyncingTracks = false;

      me.cleanUpTracks()
        .then(() => {
          me.completeAutoSync();
        })
        .catch(() => {
          me.completeAutoSync();
        });

    } else {
      // run
      me.logger.warn(me, log_label + ':run');

      me.isSyncingTracks = true;

      // 2019-03-19, toast
      if (me.loopTracks.length == 1) {
        me.presentToast(me.toastMessages['toast_sync_download_track'].replace("{{x}}", 1).replace("{{dl}}", ""));

      } else {
        me.presentToast(me.toastMessages['toast_sync_download_tracks'].replace("{{x}}", me.loopTracks.length).replace("{{dl}}", ""));
      }

      let loopTrack = me.loopTracks[0];
      me.logger.warn(me, log_label + ':run', {loopTrack: loopTrack});

      me.loadTrack(loopTrack.channel, loopTrack.track).then(() => {
        me.loopTracks.shift();

        me.trackInfo[loopTrack.channel.channel_id].files++;
        me.trackInfo[loopTrack.channel.channel_id].count++;
        me.trackInfo[loopTrack.channel.channel_id].size += loopTrack.track.file_size;
        me.triggerUpdateEventTrack();

        me.decreaseStorageSize(loopTrack.track.file_size);

        me.loopDownloadTry = 0;

        me.loopTrackDownload();

      }, error => {
        me.logger.error(me, log_label + ':loadtrack:error', {channel: loopTrack.channel, error: error});

        me.loopDownloadTry++;
        if (me.loopDownloadTry < me.maxDownloadTry) {
          // retry with delay
          let timeout: Number = 1000 * me.loopDownloadTry * me.downloadInterval;
          me.logger.info(me, log_label + ':loadtrack:wait', {
            channel: loopTrack.channel,
            loopDownloadTry: me.loopDownloadTry,
            maxDownloadTry: me.maxDownloadTry,
            timeout: timeout,
            track: loopTrack.track
          });

          setTimeout(() => {
            me.logger.info(me, log_label + ':loadtrack:waited', {
              channel: loopTrack.channel,
              loopDownloadTry: me.loopDownloadTry,
              maxDownloadTry: me.maxDownloadTry,
              timeout: timeout,
              track: loopTrack.track
            });

            me.loopTrackDownload();
          }, timeout);

        } else {
          // max tries reached
          me.logger.error(me, log_label + ':loadtrack:failed', {
            channel: loopTrack.channel,
            loopDownloadTry: me.loopDownloadTry,
            maxDownloadTry: me.maxDownloadTry,
            track: loopTrack.track
          });

          //me.isSyncingTracks = false;

          me.markTrackDefect(loopTrack.track, error).then(() => {
            me.loopTracks.shift();

            me.loopDownloadTry = 0;

            me.trackInfo[loopTrack.channel.channel_id].defect++;
            me.triggerUpdateEventTrack();

            me.loopTrackDownload();
          });
        }
      });
    }
  }

  cleanUpTracks(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::cleanUpTracks';

    me.logger.warn(me, log_label);

    me.presentToast(me.toastMessages['toast_cleanup_tracks']);

    return new Promise((resolve, reject) => {

      let channels: Array<any> = me.getChannelOrder();

      Bluebird.each(channels, (channel: Channel) => {

        return new Promise((resolve, reject) => {
          let tracks_label: string = me.getTracksLabel(channel);

          me.storage.get(tracks_label).then(tracks => {
            me.logger.debug(me, log_label + ':tracks', {tracks: tracks});

            let trackPaths: Array<string> = [];
            Object.keys(tracks).forEach(track_id => {
              trackPaths.push(tracks[track_id].entry.nativeURL);
            });
            me.logger.warn(me, log_label + ':trackPaths', {trackPaths: trackPaths});

            me.getStorageDirectory(channel).then((dir) => {
              me.logger.debug(me, log_label + ':getStorageDirectory', {dir: dir});

              me.listDir(dir.dir).then(listDir => {
                me.logger.debug(me, log_label + ':listDir', {listDir: listDir});

                if (isNullOrEmpty.isNullOrEmpty(listDir)) {
                  // empty => nothing to do
                  resolve();

                } else if (trackPaths.length < listDir.length / 2) {
                  // 2019-03-23, prevent over cleaning
                  me.logger.error(me, log_label + ':overCleaning:tracks', {
                    tracks: trackPaths.length,
                    files: listDir.length
                  });

                  resolve();

                } else {
                  // files => compare
                  let storagePaths: Array<string> = [];
                  listDir.forEach(listDirEntry => {
                    storagePaths.push(listDirEntry.nativeURL);
                  });
                  me.logger.warn(me, log_label + ':storagePaths', {storagePaths: storagePaths});

                  // diff
                  let difference = storagePaths.filter(x => !trackPaths.includes(x));
                  me.logger.error(me, log_label + ':difference', {difference: difference});

                  if (difference.length == 0) {
                    // no needless tracks
                    resolve();

                  } else if (difference.length > trackPaths.length) {
                    // 2019-03-23, prevent over cleaning
                    me.logger.error(me, log_label + ':overCleaning:difference', {
                      differences: difference.length,
                      tracks: trackPaths.length
                    });

                    resolve();

                  } else {
                    // clean up needless tracks
                    Bluebird.each(difference, nativeURL => {

                      return new Promise((resolve, reject) => {
                        me.getStorageDirectory(channel).then(dir => {
                          let storageDirectory = dir.fullPath;

                          let filename: string = nativeURL.replace(storageDirectory, "");

                          me.logger.warn(me, log_label + ':remove', {
                            storageDirectory: storageDirectory,
                            filename: filename
                          });

                          me.file.removeFile(storageDirectory, filename).then(() => {
                            resolve();

                          }, error => {
                            reject(error);
                          });
                        });
                      });

                    }).then(() => {
                      resolve();

                    }, error => {
                      reject(error);
                    })
                  }
                }

              }, error => {
                reject(error);
              });

            }, error => {
              reject(error);
            });

          }, error => {
            reject(error);
          });

        });

      }).then(() => {

        me.getFreeSpace().then(() => {

          Bluebird.each(me.channels, (channel: Channel) => {
            // files
            return me.getChannelListDir(channel).then(files => {
              me.trackInfo[channel.channel_id].files = files;
            });

          }).then(() => {
            me.triggerUpdateEventTrack();

            resolve();

          }, error => {
            reject(error);
          });

        }, error => {
          reject(error);
        });

      }, error => {
        reject(error);
      });

    });
  }

  loopTrackClearance(channel: any = null): void {
    const me = this;
    let log_label = me.log_label_main + '::loopTrackClearance';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      isSyncingTracksStopped: this.isSyncingTracksStopped,
      trackKeepList: this.trackKeepList.length
    });

    let currentChannel: any = me.getCurrentChannel(channel);

    // check stop
    /*
    if (me.isSyncingTracksStopped) {
      me.logger.warn(me, log_label + ':stopped', {channel: me.channel});

      return;
    }
    */

    // check start
    if (me.trackKeepList.length > 0) {
      //me.isSyncingTracks = true;

      me.getStorageDirectory(currentChannel).then((dir) => {
        me.logger.debug(me, log_label + ':dir', {dir: dir});

        me.listDir(dir.dir).then(listDir => {
          me.logger.debug(me, log_label + ':dir:listDir', {listDir: listDir});

          if (isNullOrEmpty.isNullOrEmpty(listDir)) {

          } else {
            let count_remove: number = 0;
            let count_error: number = 0;

            listDir.forEach(file => {
              me.logger.debug(me, log_label + ':dir:listDir:file', {file: file, file_name: file.name});

              let remove: boolean = true;
              me.trackKeepList.forEach((track) => {
                let basename: string = track.substring(track.lastIndexOf('/') + 1);

                me.logger.debug(me, log_label + ':dir:listDir:file:base', {baename: basename, file_name: file.name});
                if (basename == file.name) {
                  remove = false;
                }
              });

              me.logger.debug(me, log_label + ':dir:listDir:file:remove', {file_name: file.name, remove: remove});

              if (remove) {
                count_remove++;

                me.logger.info(me, log_label + ':dir:listDir:file:remove', {dir_name: dir.dir, file_name: file.name, remove: remove, count_remove: count_remove, count_error: count_error});

                me.file.removeFile(dir.fullPath, file.name).then(() => {
                  me.logger.info(me, log_label + ':dir:listDir:file:remove:removed');

                  me.triggerUpdateEventTrack();

                }, error => {
                  count_error++;

                  me.logger.info(me, log_label + ':dir:listDir:file:remove:error', {error: error, dir: dir, file: file, count_remove: count_remove, count_error: count_error});
                });
              }


            });
          }
        });

      }, error => {
        me.logger.error(me, log_label + ':dir:error', {error: error});
      });
      /*
      let track = me.trackDownloadList[0];
      me.loadTrack(currentChannel, track).then(() => {
        me.trackDownloadList = me.trackDownloadList.slice(1);
        me.events.publish('syncService:update');

        me.loopDownloadTry = 0;
        me.loopTrackDownload(channel);

      }, error => {
        me.logger.error(me, log_label + ':loadtrack:error', {channel: me.channel, error: error});

        me.loopDownloadTry++;
        if (me.loopDownloadTry < me.maxDownloadTry) {
          // retry with delay
          let timeout: Number = 1000 * me.loopDownloadTry * me.downloadInterval;
          me.logger.info(me, log_label + ':loadtrack:wait', {
            channel: me.channel,
            loopDownloadTry: me.loopDownloadTry,
            maxDownloadTry: me.maxDownloadTry,
            toímeout: timeout,
            track: track
          });

          setTimeout(() => {
            me.logger.info(me, log_label + ':loadtrack:waited', {
              channel: me.channel,
              loopDownloadTry: me.loopDownloadTry,
              maxDownloadTry: me.maxDownloadTry,
              toímeout: timeout,
              track: track
            });

            me.loopTrackDownload(channel);
          }, timeout);

        } else {
          // max tries reached
          me.logger.error(me, log_label + ':loadtrack:failed', {
            channel: me.channel,
            loopDownloadTry: me.loopDownloadTry,
            maxDownloadTry: me.maxDownloadTry,
            track: track
          });

          me.isSyncingTracks = false;

          me.markTrackDefect(track).then(() => {
            me.startTrackSync(false, channel);
          });
        }
      });
      */

    } else {
      me.isSyncingTracks = false;
    }
  }

  unmarkDefectTrack(track: any): void {
    const me = this;
    let log_label = me.log_label_main + '::unmarkDefectTrack';

    me.logger.warn(me, log_label);
    me.logger.warn(me, log_label, {
      track: track,
      defectTracks: me.defectTracks,
      skipDownloadTracks: me.skipDownloadTracks
    });

    if (me.defectTracks.includes(track.track_id)) {
      me.defectTracks = me.defectTracks.filter(track_id => track_id !== track.track_id);

      me.logger.warn(me, log_label, {
        defectTracks: me.defectTracks
      });

      me.events.publish('syncService:update', {track: true});
    }

    if (me.skipDownloadTracks.includes(track.track_id)) {
      me.skipDownloadTracks = me.skipDownloadTracks.filter(track_id => track_id !== track.track_id);

      me.logger.warn(me, log_label, {
        skipDownloadTracks: me.skipDownloadTracks
      });
    }
  }

  markTrackDefect(track: any, error: Error = null): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::markTrackDefect';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      track: track,
      error: error
    });

    //let tracks_label: string = me.getTracksLabel();

    return new Promise((resolve) => {

      // 2019-03-27
      let markDefect: boolean = true;
      if (!notDefined(error)) {
        if (!notDefined(error.name)) {
          if (error.name == "HttpErrorResponse") {
            me.logger.warn(me, log_label + ':cancel');
            markDefect = false;
          }
        }
      }

      if (markDefect) {
        if (!me.defectTracks.includes(track.track_id)) {
          me.defectTracks.push(track.track_id);
        }

        if (!me.skipDownloadTracks.includes(track.track_id)) {
          me.skipDownloadTracks.push(track.track_id);
        }

        // 2019-02-12
        me.events.publish('syncService:update', {track: true});

        // 2019-03-22
        if (!notDefined(error)) {
          me.reportDefectTrack(track, error);
        }
      }

      me.logger.warn(me, log_label, {
        defectTracks: me.defectTracks,
        skipDownloadTracks: me.skipDownloadTracks
      });

      resolve();

      /*me.storage.get(tracks_label).then(tracks => {
        console.log("tracks", tracks);


        /*if (isNullOrEmpty.isNullOrEmpty(tracks)) {
          tracks = {};
        }

        let result = {
          data: data,
          entry: entry,
          file: file,
          defect: false
        };

        tracks[track.track_id] = JSON.stringify(result);

        me.storage.set(tracks_label, tracks).then(() => {
          resolve(result);
        })*//*
      });*/
    });
  }

  reportDefectTrack(track: any, error: Error): void {
    const me = this;
    let log_label = me.log_label_main + '::reportDefectTrack';

    me.logger.warn(me, log_label, {track: track, error: error});
  }

  stopPlaylistSync(): void {
    const me = this;
    let log_label = me.log_label_main + '::stopPlaylistSync';

    me.logger.warn(me, log_label);

    me.isSyncingPlaylistsStopped = true;
    me.isSyncingPlaylists = false;

    me.cancelSyncToast();
  }

  getCurrentChannel(channel: any = null): any {
    const me = this;
    let log_label = me.log_label_main + '::getCurrentChannel';

    me.logger.warn(me, log_label);

    let currentChannel = me.channel;
    if (!isNullOrEmpty.isNullOrEmpty(channel)) {
      currentChannel = channel;
    }

    return currentChannel;
  }

/*
  preparePlaylistSync(channel: any = null): void {
    const me = this;
    let log_label = me.log_label_main + '::preparePlaylistSync';

    me.logger.warn(me, log_label);

    let currentChannel: any = me.getCurrentChannel(channel);

    me.logger.info(me, log_label, {
      currentChannel: currentChannel,
      channel: channel
    });

    me.lastPlaylistUpdate[currentChannel.channel_id] = null;
    me.defectPlaylists[currentChannel.channel_id] = {};

    me.startPlaylistSync(true, channel);
  }
*/

  addFakePlaylists(channel: any = null): void {
    const me = this;
    let log_label = me.log_label_main + '::addFakePlaylists';

    me.logger.warn(me, log_label);

    let currentChannel: any = me.getCurrentChannel(channel);

    let playlists_label: string = me.getPlaylistsLabel(currentChannel);

    let playlist_label_dev: string = playlists_label + "::" + "pl_" + moment().subtract(14, "days").format("YYYY-MM-DD");
    let playlist_dev: any = [];
    me.storage.set(playlist_label_dev, JSON.stringify(playlist_dev)).then(() => {
      me.logger.warn(me, log_label + ':dev:saved', {playlist_label_dev: playlist_label_dev});

      me.triggerUpdateEventPlaylist();
    });
  }

  clearNextTrack(): void {
    const me = this;
    let log_label = me.log_label_main + '::clearNextTrack';

    me.logger.warn(me, log_label);

    if (me.next[0]) {
      let track: any = me.next[0];

      me.logger.warn(me, log_label, {track: track, next: me.next.length});

      let fileName = me.getFileName(track);

      me.getStorageDirectory(me.globalvarsProvider.selectedChannel).then(dir => {
        let storageDirectory = dir.fullPath;

        me.file.checkFile(storageDirectory, fileName).then(exists => {
          me.logger.warn(me, log_label + ":checkFile", {exists: exists});

          me.file.resolveDirectoryUrl(storageDirectory).then(dirEntry => {
            me.file.getFile(dirEntry, fileName, {create: false}).then(entry => {
              me.logger.warn(me, log_label + ":getfile", {entry: entry});

              me.file.removeFile(storageDirectory, entry.name).then(result => {
                me.logger.warn(me, log_label + ":removeFile", {result: result});

                // 2019-03-27
                //me.markTrackDefect(track);

              }, error => {
                me.logger.error(me, log_label + ":removeFile", {error: error});
              });

            }, error => {
              me.logger.error(me, log_label + ":getfile", {error: error});
            });

          }, error => {
            me.logger.error(me, log_label + ":resolveDirectoryUrl", {error: error});
          });

        }, error => {
          me.logger.error(me, log_label + ":checkFile", {error: error});
        });

      }, error => {
        me.logger.error(me, log_label + ":getStorageDirectory", {error: error});
      });
    }
  }

  private getChannelListDir(channel: Channel): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getChannelListDir';

    me.logger.warn(me, log_label);

    return new Promise((resolve, reject) => {
      me.getStorageDirectory(channel).then((dir) => {
        me.logger.debug(me, log_label + ':getStorageDirectory', {dir: dir});

        me.listDir(dir.dir).then(listDir => {
          me.logger.debug(me, log_label + ':listDir', {listDir: listDir});

          resolve(listDir.length);

        }, error => {
          reject(error);
        });

      }, error => {
        reject(error);
      });
    });
  }

  getChannelOrder(): Array<any> {
    const me = this;
    let log_label = me.log_label_main + '::getChannelOrder';

    me.logger.warn(me, log_label, {channels: me.channels});
    let channels: Array<any> = [];

    if (me.globalvarsProvider.selectedChannel) {
      channels.push(me.globalvarsProvider.selectedChannel);

      me.channels.forEach((channel: Channel) => {
        if (channel.channel_id != channels[0].channel_id) {
          channels.push(channel);
        }
      });

    } else {
      channels = me.channels;
    }
    me.logger.warn(me, log_label, {channels: channels});

    return channels;
  }

  preparePlaylistLoop(): void {
    const me = this;
    let log_label = me.log_label_main + '::preparePlaylistLoop';

    me.logger.warn(me, log_label);

    // 2019-03-22
    if (me.isSyncingPlaylists) {
      return;
    }

    // 2020-04-29, prevent empty last sync
    if (isNullOrEmpty(me.lastSyncBegin)) {
      me.lastSyncBegin = moment();
    }

    me.isSyncingPlaylists = true;
    me.isSyncingPlaylistsStopped = false;
    me.loopPlaylists = [];

    // 2019-03-23
    //me.stopAutoSyncTimeout();

    // 2019-03-19, toast
    me.presentToast(me.toastMessages['toast_sync_prepare_playlists']);

    let playlists: Array<any> = [];

    // 2019-03-18, channel order
    let channels: Array<any> = me.getChannelOrder();

    // load all playlists from all channels
    Bluebird.each(channels, (ch: Channel) => {

      return new Promise((resolve, reject) => {

        me.setPlaylistInfo(ch);

        me.loadPlaylists(ch).then((data) => {
          me.logger.warn(me, log_label + ':loaded', {channel_id: ch.channel_id});
          me.logger.info(me, log_label + ':loaded', {data: data});

          me.getPlaylistsResult(data.data, data.fallback, data.meta.channel.id, false)
            .then(() => {})
            .catch(() => {});

          playlists.push(data);

          resolve();

        }, error => {
          reject(error);
        });

      });

    }).then(() => {
      me.logger.warn(me, log_label + ":promises:done");

      me.storePlaylistsAndFallbacks(playlists).then((data) => {
        me.logger.warn(me, log_label + ":storePlaylistsAndFAllbacks:done", {data: data, playlistInfo: me.playlistInfo});

        // storage keys
        me.storage.keys().then((keys) => {
          me.logger.warn(me, log_label + ":keys:done");
          me.logger.info(me, log_label + ":keys:done", {keys: keys});

          keys.forEach((key) => {
            if (
              key.includes("playlists_")
              ||
              key.includes("fallbacks_")
            ) {
              if (!data.labels.includes(key)) {
                // delete
                me.logger.warn(me, log_label + ":key:delete", {key: key});

                me.storage.remove(key)
                  .then(() => {})
                  .catch(() => {});
              }
            }
          });

          // storage playlists
          me.checkPlaylistsAndFallbacks(keys, data).then(() => {
            me.logger.warn(me, log_label + ":checkPlaylistsAndFAllbacks:done");

            me.loopPlaylistDownload();

          }, error => {
            me.logger.error(me, log_label + ":checkPlaylistsAndFAllbacks:error", {error: error});

            me.cancelSyncToast();
          });

        }, error => {
          me.logger.error(me, log_label + ":keys:done:error", {error: error});

          me.cancelSyncToast();
        });

      }, error => {
        me.logger.error(me, log_label + ":storePlaylistsAndFAllbacks:done:error", {error: error});

        me.cancelSyncToast();
      });

    }, error => {
      me.logger.error(me, log_label + ":promises:error", {error: error});

      me.cancelSyncToast();
    });
  }

  storePlaylistsAndFallbacks(playlists: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::storePlaylistsAndFallbacks';

    me.logger.warn(me, log_label);

    let list: any = {};
    let labels: Array<any> = [];

    return new Promise((resolve, reject) => {

      //if (me.toast) me.presentToast(me.toastMessages['toast_verify_playlists_and_fallbakcs']);

      //playlists.forEach((playlist) => {
      Bluebird.each(playlists, (playlist) => {

        return new Promise((resolve, reject) => {
          me.logger.warn(me, log_label + ":promises:playlist", {playlist: playlist});

          let playlists_label: string = me.getPlaylistsLabel(playlist.meta.channel.id);
          labels.push(playlists_label);

          let fallbacks_label: string = me.getFallbacksLabel(playlist.meta.channel.id);
          labels.push(fallbacks_label);

          //promises.push(new Promise((resolve, reject) => {
          me.storage.set(playlists_label, playlist.data).then(() => {
            me.logger.warn(me, log_label + ":playlist:done", {playlists_label: playlists_label});

            playlist.data.forEach((pl) => {
              let label: string = playlists_label + '::' + pl.name;
              pl.channel_id = playlist.meta.channel.id;
              pl.label = label;
              pl.fallback = false;
              list[label] = pl;

              labels.push(label);
            });

            me.playlistInfo[playlist.meta.channel.id].playlists.total = playlist.data.length;
            me.triggerUpdateEventPlaylist();

            me.storage.set(fallbacks_label, playlist.fallback).then(() => {
              me.logger.warn(me, log_label + ":fallback:done", {fallbacks_label: fallbacks_label});

              playlist.fallback.forEach((pl) => {
                let label: string = fallbacks_label + '::' + pl.name;
                pl.channel_id = playlist.meta.channel.id;
                pl.label = label;
                pl.fallback = true;
                list[label] = pl;

                labels.push(label);
              });

              me.playlistInfo[playlist.meta.channel.id].fallbacks.total = playlist.fallback.length;
              me.triggerUpdateEventPlaylist();

              resolve();

            }, error => {
              me.logger.error(me, log_label + ":fallback:error", {error: error});
              reject(error);
            });

          }, error => {
            me.logger.error(me, log_label + ":playlist:error", {error: error});
            reject(error);
          });
          //}));
        });

      }).then(() => {

      //Promise.all(promises).then(() => {
        resolve({
          list: list,
          labels: labels
        });

      }, error => {
        reject(error);
      })

    });
  }

  checkPlaylistsAndFallbacks(keys: any, data: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::checkPlaylistsAndFallbacks';

    me.logger.warn(me, log_label);

    return new Promise((resolve, reject) => {

      let length: number = Object.keys(data.list).length;

      Bluebird.each(Object.keys(data.list), (pl) => {
        let playlist: Playlist = data.list[pl];

        return new Promise((resolve, reject) => {

          if (me.toast) {
            if (length == 1) {
              me.presentToast(me.toastMessages['toast_verify_playlist'].replace('{{x}}', 1));
            } else {
              me.presentToast(me.toastMessages['toast_verify_playlists'].replace('{{x}}', length));
            }
          }

          if (keys.includes(pl)) {
            // check
            me.logger.info(me, log_label + ":pl:check", {pl: pl, playlist: playlist});

            me.storage.get(pl).then(result => {
              me.logger.info(me, log_label + ":pl:check:result", {result: result});

              let checksumPlaylist: any = me.createChecksumPlaylist(result);
              let checksum: string = CryptoJS.MD5(JSON.stringify(checksumPlaylist)).toString();

              if (checksum != playlist.checksum) {
                // download
                me.logger.info(me, log_label + ":pl:download", {pl: pl, playlist: playlist});

                me.loopPlaylists.push(playlist);

              } else {
                // correct

                // playlistInfo
                me.logger.info(me, log_label + ":pl:playlistInfo", {playlist: playlist, playlistInfo: me.playlistInfo});
                if (playlist.fallback) {
                  me.playlistInfo[playlist.channel_id].fallbacks.count++;
                } else {
                  me.playlistInfo[playlist.channel_id].playlists.count++;
                  me.checkMinMaxDate(playlist);
                }
                me.triggerUpdateEventPlaylist();

              }

              resolve();

            }, error => {
              me.logger.error(me, log_label + ":pl:check:error", {error: error});
              reject(error);
            });

          } else {
            // download
            me.logger.info(me, log_label + ":pl:download", {pl: pl, playlist: playlist});

            me.loopPlaylists.push(playlist);

            resolve();
          }

          length--;
        });

      }).then(() => {
        resolve();

      }, error => {
        reject(error);
      })

    });
  }

  loopPlaylistDownload(): void {
    const me = this;
    let log_label = me.log_label_main + '::loopPlaylistDownload';

    me.logger.warn(me, log_label, {loopPlaylists: me.loopPlaylists.length});

    // check stop
    if (me.isSyncingPlaylistsStopped) {
      me.logger.warn(me, log_label + ':stopped');

      me.completeAutoSync();

      return;
    }

    if (me.loopPlaylists.length == 0) {
      // finish
      me.logger.warn(me, log_label + ':finish', {playlistInfo: me.playlistInfo});

      me.isSyncingPlaylists = false;

      me.setMinMaxDate();

      me.prepareTrackLoop();

    } else {
      // run
      me.logger.info(me, log_label + ':run');

      me.isSyncingPlaylists = true;

      // 2019-03-19, toast
      if (me.loopPlaylists.length == 1) {
        me.presentToast(me.toastMessages['toast_sync_download_playlist'].replace("{{x}}", 1));

      } else {
        me.presentToast(me.toastMessages['toast_sync_download_playlists'].replace("{{x}}", me.loopPlaylists.length));
      }

      let pl: Playlist = me.loopPlaylists.shift();
      me.logger.info(me, log_label + ':run', {pl: pl});

      me.loadPlaylist({channel_id: pl.channel_id}, pl.id, pl.name, pl.checksum).then(result => {
        me.storage.set(pl.label, result).then(() => {

          // playlistInfo
          if (pl.fallback) {
            me.playlistInfo[pl.channel_id].fallbacks.count++;
          } else {
            me.playlistInfo[pl.channel_id].playlists.count++;
            me.checkMinMaxDate(pl);
          }
          me.triggerUpdateEventPlaylist();

          me.loopPlaylistDownload();

        }, () => {
          // playlistInfo
          if (!pl.fallback) {
            me.playlistInfo[pl.channel_id].playlists.error++;
            me.triggerUpdateEventPlaylist();
          }

          me.loopPlaylistDownload();
        });

      }, () => {
        // playlistInfo
        if (!pl.fallback) {
          me.playlistInfo[pl.channel_id].playlists.error++;
          me.triggerUpdateEventPlaylist();
        }

        me.loopPlaylistDownload();
      });
    }
  }

  setPlaylistInfo(channel: Channel, tracks: boolean = true, fallbacks: boolean = true): void {
    const me = this;
    let log_label = me.log_label_main + '::setPlaylistInfo';

    me.logger.warn(me, log_label, {tracks: tracks, fallbacks: fallbacks});

    if (!me.playlistInfo[channel.channel_id]) {
      // create
      me.playlistInfo[channel.channel_id] = {
        playlists: {
          count: 0,
          total: 0,
          diff: 0,
          defect: 0,
          start: null,
          end: null
        },
        fallbacks: {
          count: 0,
          total: 0
        }
      };

    } else {
      // reset
      if (tracks) {
        me.playlistInfo[channel.channel_id].playlists.count = 0;
        me.playlistInfo[channel.channel_id].playlists.diff = 0;
        me.playlistInfo[channel.channel_id].playlists.defect = 0;
      }

      if (fallbacks) {
        me.playlistInfo[channel.channel_id].fallbacks.count = 0;
      }

    }
  }

  setTrackInfo(channel: Channel): void {
    const me = this;
    let log_label = me.log_label_main + '::setTrackInfo';

    me.logger.warn(me, log_label);

    if (!me.trackInfo[channel.channel_id]) {
      // create
      me.trackInfo[channel.channel_id] = {
        count: 0,
        total: 0,
        diff: 0,
        defect: 0,
        files: 0,
        size: 0
      };

    } else {
      // reset
      me.trackInfo[channel.channel_id].count = 0;
      me.trackInfo[channel.channel_id].diff = 0;
      me.trackInfo[channel.channel_id].defect = 0;
      me.trackInfo[channel.channel_id].files = 0;
      me.trackInfo[channel.channel_id].size = 0;

    }
  }

  checkMinMaxDate(playlist: Playlist): void {
    const me = this;
    let log_label = me.log_label_main + '::checkMinMaxDate';

    me.logger.info(me, log_label, {playlist: playlist});

    if (!playlist.fallback) {
      // min
      if (!me.minDates[playlist.channel_id]) {
        me.minDates[playlist.channel_id] = playlist.date.unix;

      } else if (playlist.date.unix < me.minDates[playlist.channel_id]) {
        me.minDates[playlist.channel_id] = playlist.date.unix;
      }

      // max
      if (!me.maxDates[playlist.channel_id]) {
        me.maxDates[playlist.channel_id] = playlist.date.unix;

      } else if (playlist.date.unix > me.maxDates[playlist.channel_id]) {
        me.maxDates[playlist.channel_id] = playlist.date.unix;
      }
    }
  }

  setMinMaxDate(): void {
    const me = this;
    let log_label = me.log_label_main + '::setMinMaxDate';

    me.logger.warn(me, log_label, {minDates: me.minDates, maxDates: me.maxDates, playlistInfo: me.playlistInfo});

    Object.keys(me.minDates).forEach(channel_id => {
      let minDateDate: any = moment.unix(me.minDates[channel_id]);

      me.playlistInfo[channel_id].playlists.start = minDateDate;
    });

    me.minDates = {};

    Object.keys(me.maxDates).forEach(channel_id => {
      let maxDateDate: any = moment.unix(me.maxDates[channel_id]);

      me.playlistInfo[channel_id].playlists.end = maxDateDate;
    });

    me.maxDates = {};

    me.triggerUpdateEventPlaylist();
  }

  prepareTrackLoop(): void {
    const me = this;
    let log_label = me.log_label_main + '::prepareTrackLoop';

    me.logger.warn(me, log_label);

    // 2019-03-22
    if (me.isSyncingTracks) {
      return;
    }

    // 2020-04-29, prevent empty last sync
    if (isNullOrEmpty(me.lastSyncBegin)) {
      me.lastSyncBegin = moment();
    }

    me.isSyncingTracks = true;
    me.isSyncingTracksStopped = false;
    me.loopTracks = [];

    // 2019-03-23
    //me.stopAutoSyncTimeout();

    // 2019-03-19, toast
    me.presentToast(me.toastMessages['toast_sync_prepare_tracks']);

    // 2019-03-18, channel order
    let channels: Array<any> = me.getChannelOrder();

    // load all playlists from all channels
    Bluebird.each(channels, (ch: Channel) => {

      let promises: Array<any> = [];

      return new Promise((resolve, reject) => {
        // init unique tracks
        me.uniqueTracks[ch.channel_id] = {};

        me.setTrackInfo(ch);

        // files
        promises.push(me.getChannelListDir(ch).then(files => {
          me.trackInfo[ch.channel_id].files = files;
        }));

        // playlists
        promises.push(new Promise((resolve, reject) => {
          let playlists_label: string = me.getPlaylistsLabel(ch);

          me.storage.get(playlists_label).then(playlists => {
            me.logger.warn(me, log_label + ":playlists", {playlists: playlists});

            me.findUniqueTracks(ch, playlists, playlists_label).then(() => {
              resolve();

            }, error => {
              // 2019-11-25, changed error handling
              me.logger.warn(me, log_label + ":findUniqueTracks:error", {error: error});
              resolve();

              // reject(error);
            });

          }, error => {
            reject(error);
          });
        }));

        // fallbacks
        promises.push(new Promise((resolve, reject) => {
          let fallbacks_label: string = me.getFallbacksLabel(ch);

          me.storage.get(fallbacks_label).then(fallbacks => {
            me.logger.warn(me, log_label + ":fallbacks", {fallbacks: fallbacks});

            me.findUniqueTracks(ch, fallbacks, fallbacks_label, true).then(() => {
              resolve();

            }, error => {
              // 2019-11-25, changed error handling
              me.logger.warn(me, log_label + ":findUniqueTracks:fallback:error", {error: error});
              resolve();

              // reject(error);
            });

          }, error => {
            reject(error);
          });

        }));

        Promise.all(promises).then(() => {
          resolve();

        }, error => {
          reject(error);
        });

      });

    }).then(() => {
      me.logger.warn(me, log_label + ":promises:done", {uniqueTracks: me.uniqueTracks});

      me.storeAndCheckTracks().then(() => {
        me.logger.warn(me, log_label + ":storeAndCheckTracks:done", {trackInfo: me.trackInfo});

        // TODO: order of downloads
        me.orderDownloads().then(() => {
          me.logger.warn(me, log_label + ":orderDownloads:done");

          me.loopTrackDownload();

        }, error => {
          me.logger.error(me, log_label + ":orderDownloads:error", {error: error});

          me.cancelSyncToast();
        });

      }, error => {
        me.logger.error(me, log_label + ":storeAndCheckTracks:error", {error: error});

        me.cancelSyncToast();
      });

    }, error => {
      me.logger.error(me, log_label + ":promises:error", {error: error});

      me.cancelSyncToast();
    });
  }

  storeAndCheckTracks(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::storeAndCheckTracks';

    me.logger.warn(me, log_label);

    //let promises: Array<any> = [];

    return new Promise((resolve, reject) => {

      // 2019-03-18, channel order
      let channels: Array<any> = me.getChannelOrder();

      // load all playlists from all channels
      Bluebird.each(channels, (channel: Channel) => {
      //me.channels.forEach((channel: Channel) => {
        //promises.push(new Promise((resolve, reject) => {
        return new Promise((resolve, reject) => {

          // track info
          me.trackInfo[channel.channel_id].total = Object.keys(me.uniqueTracks[channel.channel_id]).length;
          me.triggerUpdateEventTrack();

          // check storage
          me.storeAndCheckTracksStorage(channel).then(tracks => {

            // check unique
            me.storeAndCheckTracksUnique(channel, tracks).then(() => {
              resolve();

            }, error => {
              reject(error);
            });

          }, error => {
            reject(error);
          });

        });
        //}));

      }).then(() => {

      //Promise.all(promises).then(() => {
        resolve();

      }, error => {
        reject(error);
      });
    });
  }

  storeAndCheckTracksStorage(channel: Channel): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::storeAndCheckTracksStorage';

    me.logger.warn(me, log_label);

    return new Promise((resolve, reject) => {
      let storage_label: string = me.getTracksLabel(channel);

      me.storage.get(storage_label).then(tracks => {
        me.logger.warn(me, log_label + ":track", {tracks: tracks});

        // check tracks
        if (notDefined(tracks)) {
          resolve(null);

        } else {
          me.logger.warn(me, log_label + ":track", {tracksLength: Object.keys(tracks).length});

          Object.keys(tracks).forEach(track_id => {
            if (!me.uniqueTracks[channel.channel_id][track_id]) {
              // delete
              me.logger.info(me, log_label + ":track:delete");
              delete tracks[track_id];
            }
          });

          me.storage.set(storage_label, tracks).then(() => {
            resolve(tracks);

          }, error => {
            reject(error);
          });
        }
      }, error => {
        reject(error);
      });
    });
  }

  storeAndCheckTracksUnique(channel: Channel, tracks: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::storeAndCheckTracksUnique';

    me.logger.warn(me, log_label, {channel_id: channel.channel_id});

    //let promises: Array<any> = [];

    return new Promise((resolve, reject) => {

      me.getStorageDirectory(channel).then(storageDirEntry => {

        let length: number = Object.keys(me.uniqueTracks[channel.channel_id]).length;

        // check unique tracks
        //Object.keys(me.uniqueTracks[channel.channel_id]).forEach(track_id => {
        Bluebird.each(Object.keys(me.uniqueTracks[channel.channel_id]), track_id => {
          let track: any = me.uniqueTracks[channel.channel_id][track_id];

          if (me.toast) {
            if (length == 1) {
              me.presentToast(me.toastMessages['toast_verify_track'].replace('{{x}}', 1).replace('{{ch}}', me.getShortChannelName(channel)));
            } else {
              me.presentToast(me.toastMessages['toast_verify_tracks'].replace('{{x}}', length).replace('{{ch}}', me.getShortChannelName(channel)));
            }
          }

          return new Promise((resolve) => {
            if (
              !notDefined(tracks)
              &&
              tracks[track_id]
            ) {
              // compare track with local file
              let storedTrack: any = tracks[track_id];
              me.logger.info(me, log_label + ":track:checkFile", {track: track, storedTrack: storedTrack});

              if (track.file_size == storedTrack.data.size) {
                me.storeAndCheckTracksUniqueCheckFile(channel, storageDirEntry, track, tracks[track_id]).then((size) => {
                  me.logger.info(me, log_label + ':uniqueSize', {size: size, tracks: tracks[track_id]});

                  // ok
                  me.trackInfo[channel.channel_id].count++;
                  me.trackInfo[channel.channel_id].size += size;
                  me.triggerUpdateEventTrack();

                  //me.decreaseStorageSize(size);

                  resolve();

                }, () => {
                  // download
                  me.pushTrackDownload(channel, track);

                  resolve();

                });

              } else {
                // download
                me.pushTrackDownload(channel, track);

                resolve();

              }

            } else {
              // look for local track
              me.logger.info(me, log_label + ":track:lookupFile", {track: track});
              me.storeAndCheckTracksUniqueLookupFile(channel, storageDirEntry, track).then((size) => {
                // ok
                me.trackInfo[channel.channel_id].count++;
                me.trackInfo[channel.channel_id].size += size;
                me.triggerUpdateEventTrack();

                resolve();

              }, () => {
                // download
                me.pushTrackDownload(channel, track);

                resolve();

              });

            }

            length--;
          });

        }).then(() => {
          resolve();

        }, error => {
          reject(error);
        });

      }, error => {
        reject(error);
      });

    });
  }

  pushTrackDownload(channel: Channel, track: any): void {
    const me = this;
    let log_label = me.log_label_main + '::pushTrackDownload';

    me.logger.info(me, log_label);

    if (
      track
      &&
      !me.defectTracks.includes(track.track_id)
    ) {
      me.loopTracks.push({
        channel: channel,
        track: track
      });

    } else {
      me.trackInfo[channel.channel_id].defect++;
      me.triggerUpdateEventTrack();
    }
  }

  private checkOldFile(storageDirEntry: any, filename: string, filenameOld: string): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::checkOldFile';

    me.logger.info(me, log_label);

    let storageDirectory: string = storageDirEntry.fullPath;

    return new Promise((resolve, reject) => {

      // check for old tracknames without track id
      me.file.checkFile(storageDirectory, filenameOld).then(exists => {
        me.logger.info(me, log_label + ":checkFileOld", {exists: exists, filenameOld: filenameOld, storageDirectory: storageDirectory});

        me.file.checkFile(storageDirectory, filename).then(exists => {
          me.logger.info(me, log_label + ":checkFile", {exists: exists, filename: filename, storageDirectory: storageDirectory});

          // new and old exists => delete
          me.file.removeFile(storageDirectory, filenameOld).then(() => {

            me.file.resolveDirectoryUrl(storageDirectory).then(dirEntry => {
              me.file.getFile(dirEntry, filename, {create: false}).then(fileEntry => {
                resolve(fileEntry);

              }, error => {
                reject(error);
              });

            }, error => {
              reject(error);
            });

          }, error => {
            reject(error);
          });

        }, () => {
          // only old exists => rename
          me.file.moveFile(storageDirectory, filenameOld, storageDirectory, filename).then((fileEntry) => {
            resolve(fileEntry);

          }, error => {
            reject(error);
          });
        });

      }, () => {
        // does not exists => ok
        me.file.checkFile(storageDirectory, filename).then(exists => {
          me.logger.info(me, log_label + ":checkFile", {exists: exists, filename: filename, storageDirectory: storageDirectory});

          me.file.resolveDirectoryUrl(storageDirectory).then(dirEntry => {
            me.file.getFile(dirEntry, filename, {create: false}).then(fileEntry => {
              resolve(fileEntry);

            }, error => {
              reject(error);
            });

          }, error => {
            reject(error);
          });

        }, error => {
          reject(error);
        });

      });
    });
  }

  storeAndCheckTracksUniqueCheckFile(channel: Channel, storageDirEntry: any, track: any, storageTrack: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::storeAndCheckTracksUniqueCheckFile';

    me.logger.info(me, log_label);

    return new Promise((resolve, reject) => {
      let filename: string = me.getFileName(track);
      let filenameOld: string = me.getFileName(track, true);

      //let storageDirectory: string = storageDirEntry.fullPath;

      me.logger.info(me, log_label + ':files', {filename: filename, filenameOld: filenameOld});

      // check for old tracknames without track id
      me.checkOldFile(storageDirEntry, filename, filenameOld).then((fileEntry) => {
        me.logger.debug(me, log_label + ":getFile", {fileEntry: fileEntry});

        me.getFileSize(storageDirEntry, fileEntry).then(size => {
          me.logger.info(me, log_label + ":getFileSize", {
            filename: filename,
            size: size,
            track: track,
            storageTrack: storageTrack,
            fileEntry: fileEntry
          });

          if (size == track.file_size) {
            if (fileEntry.fullPath == storageTrack.entry.fullPath) {
              // matching
              resolve(size);

            } else {
              // moved
              fileEntry.file(file => {
                storageTrack.entry.fullPath = fileEntry.fullPath;
                storageTrack.entry.nativeURL = fileEntry.nativeURL;

                storageTrack.file.localURL = file.localURL;

                let tracks_label: string = me.getTracksLabel(channel);

                me.storage.get(tracks_label).then(storageTracks => {

                  storageTracks[track.track_id] = storageTrack;

                  me.storage.set(tracks_label, storageTracks).then(() => {
                    resolve();

                  },error => {
                    reject(error);
                  });

                },error => {
                  reject(error);
                });

              }, error => {
                reject(error);
              });
            }

          } else {
            reject(new Error("file size mismatch (" + size + " / " + track.file_size + ")"));
          }

        }, error => {
          reject(error);
        });

      }, error => {
        reject(error);
      });

    });
  }

  storeAndCheckTracksUniqueLookupFile(channel: Channel, storageDirEntry: any, track: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::storeAndCheckTracksUniqueLookupFile';

    me.logger.info(me, log_label);

    return new Promise((resolve, reject) => {
      let filename: string = me.getFileName(track);
      let filenameOld: string = me.getFileName(track, true);

      let storageDirectory: string = storageDirEntry.fullPath;

      me.logger.info(me, log_label + ':files', {filename: filename, filenameOld: filenameOld, storageDirectory: storageDirectory});

      // check for old tracknames without track id
      me.checkOldFile(storageDirEntry, filename, filenameOld).then((fileEntry) => {
        me.logger.debug(me, log_label + ":getFile", {fileEntry: fileEntry, track: track});

        me.getFileSize(storageDirEntry, fileEntry, true).then(meta => {
          me.logger.info(me, log_label + ":getFileSize", {meta: meta, track: track});

          me.logger.debug(me, log_label + ':storeLoadedTrack', {
            channel: channel,
            track: track,
            fileEntry: fileEntry,
            meta: meta
          });

          me.storeLoadedTrack(channel, track, meta, fileEntry).then(() => {
            resolve(meta.size);

          }, error => {
            me.logger.error(me, log_label + ":storeLoadedTrack:error", {error: error});
            reject(error);
          });

        }, error => {
          me.logger.error(me, log_label + ":getFileSize:error", {error: error});
          reject(error);
        });

      }, error => {
        //me.logger.error(me, log_label + ":checkOldFile:error", {error: error});
        reject(error);
      });

    });
  }

  orderDownloads(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::orderDownloads';

    me.logger.warn(me, log_label);

    let currents: Array<any> = [];

    return new Promise((resolve, reject) => {

      // 2019-03-18, channel order
      let channels: Array<any> = me.getChannelOrder();

      // load all playlists from all channels
      Bluebird.each(channels, (channel: Channel) => {

        return new Promise((resolve, reject) => {
          me.getCurrentPlaylist(channel, false).then((playlist) => {
            me.logger.warn(me, log_label + ':getCurrentPlaylist:done', {playlist: playlist});

            me.getCurrentPlaylistTracks(false, playlist.playlist, playlist.playlist_date, playlist.playlist_next, playlist.playlist_date_next).then((current) => {
              me.logger.warn(me, log_label + ':getCurrentPlaylistTracks:done', {current: current});

              current.channel = channel;
              currents.push(current);

              resolve();

            }, error => {
              reject(error);
            });

          }, error => {
            reject(error);
          });

        });

      }).then(() => {
        me.logger.warn(me, log_label + ':done', {loopTracks: me.loopTracks});

        me.logger.warn(me, log_label + ':done', {currents: currents});

        let downloadIds: Array<any> = [];

        me.loopTracks.forEach((loop) => {
          downloadIds.push(loop.track.track_id);
        });

        let loopTracks: Array<any> = [];
        let trackIds: Array<any> = [];

        currents.forEach((current) => {
          me.logger.warn(me, log_label + ':done', {current: current});

          if (downloadIds.includes(current.current.track_id)) {
            loopTracks.push({
              channel: current.channel,
              track: current.current
            });
            trackIds.push(current.current.track_id);
          }

          current.next.forEach((next) => {
            if (downloadIds.includes(next.track_id)) {
              loopTracks.push({
                channel: current.channel,
                track: next
              });
              trackIds.push(next.track_id);
            }
          });
        });

        downloadIds = null;

        me.loopTracks.forEach((loop) => {
          if (!trackIds.includes(loop.track.track_id)) {
            loopTracks.push(loop);
          }
        });

        me.loopTracks = [];

        loopTracks.forEach((loop) => {
          me.pushTrackDownload(loop.channel, loop.track);
        });

        loopTracks = null;
        trackIds = null;

        resolve(currents);

      }, error => {
        reject(error);
      });

    });
  }

  findUniqueTracks(channel: Channel, playlists: Array<Playlist>, label: string, fallback: boolean = false): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::findUniqueTracks';

    me.logger.warn(me, log_label);

    return new Promise((resolve, reject) => {

      let promises: Array<any> = [];

      playlists.forEach(playlist => {
        promises.push(new Promise((resolve, reject) => {
          let storage_label: string = label + '::' + playlist.name;

          me.storage.get(storage_label).then(data => {
            if (notDefined(data)) {
              reject(new Error(storage_label + ' is empty'));

            } else {
              data.forEach((playlistItem: PlaylistItem) => {
                me.logger.debug(me, log_label + ':playlistItem', {playlistItem: playlistItem, fallback: fallback});

                if (!me.uniqueTracks[channel.channel_id]) {
                  me.uniqueTracks[channel.channel_id] = {};
                }

                // 2019-03-22
                if (fallback) {
                  playlistItem.fallback = true;
                }

                if (!me.uniqueTracks[channel.channel_id][playlistItem.track_id]) {
                  me.uniqueTracks[channel.channel_id][playlistItem.track_id] = playlistItem;
                }
              });

              resolve();
            }

          }, error => {
            reject(error);
          });
        }));
      });

      Promise.all(promises).then(() => {
        resolve();

      }, error => {
        reject(error);
      });
    });
  }

/*
  startPlaylistSync(force: boolean = false, channel: any = null): void {
    const me = this;
    let log_label = me.log_label_main + '::startPlaylistSync';

    me.logger.warn(me, log_label);
    me.logger.warn(me, log_label, {
      force: force,
      channel_id: channel.channel_id,
      channels: me.channels.length
    });

    let currentChannel: any = me.getCurrentChannel(channel);

    // SIA-20, 2019-02-03, sync over all channels

    me.logger.debug(me, log_label, {
      currentChannel: currentChannel,
      channel: me.channel,
      force: force,
      isSyncingPlaylistsStopped: me.isSyncingPlaylistsStopped,
      isSyncingPlaylists: me.isSyncingPlaylists,
      isSyncingPlaylistsCount: me.isSyncingPlaylistsCount,
      devMode: me.globalvarsProvider.devMode
    });

    let playlists_label: string = me.getPlaylistsLabel(currentChannel); // "playlists_" + me.channel.channel_id;
    let fallbacks_label: string = me.getFallbacksLabel(currentChannel); // "fallbacks_" + me.channel.channel_id;
    let playlist_to_sync        = null;
    let playlist_to_sync_label  = null;
    let playlist_to_sync_hash   = null;

    me.logger.info(me, log_label, {
      playlists_label: playlists_label,
      fallbacks_label: fallbacks_label,
      force: force
    });

    // check stop
    if (!force && me.isSyncingPlaylistsStopped) {
      me.logger.warn(me, log_label + ':stopped', {
        currentChannel: currentChannel,
        channel: me.channel
      });

      return;

    } else {
      me.isSyncingPlaylistsStopped = false;
    }

    // check start
    if (!me.isSyncingPlaylists) {
      me.isSyncingPlaylistsCount = 0;
    }
    me.isSyncingPlaylists = true;

    // dev mode add test playlist
    if (me.globalvarsProvider.devMode && me.globalvarsProvider.addPlaylistTest) {
      me.addFakePlaylists(currentChannel);
    }

    // run
    me.updateChannelPlaylist(playlists_label, fallbacks_label, currentChannel).then(() => {
      me.storage.keys().then(keys => {
        me.logger.info(me, log_label + ':keys', {keys_type: typeof keys, keys: keys});

        // playlists
        me.playlists[currentChannel.channel_id].forEach(playlist => {
          me.logger.debug(me, log_label + ':playlist', {playlist: playlist});

          let playlist_label  = playlists_label + "::" +playlist.name;
          let playlist_hash   = playlist.checksum;
          let playlist_defect = false;

          if (
            me.defectPlaylists[currentChannel.channel_id]
            &&
            me.defectPlaylists[currentChannel.channel_id][playlist_label]
          ) {
            if (me.defectPlaylists[currentChannel.channel_id][playlist_label] >= me.maxSyncTry) {
              playlist_defect = true;
            }
          }

          if (
            isNullOrEmpty.isNullOrEmpty(playlist_to_sync)
            &&
            !keys.includes(playlist_label)
            &&
            !playlist_defect
          ) {
            playlist_to_sync        = playlist;
            playlist_to_sync_label  = playlist_label;
            playlist_to_sync_hash   = playlist_hash;

            me.logger.debug(me, log_label + ':playlist_to_sync', {playlist_to_sync_label: playlist_to_sync_label, playlist_to_sync: playlist_to_sync});
          }
        });

        // fallbacks
        me.fallbacks[currentChannel.channel_id].forEach(fallback => {
          me.logger.debug(me, log_label + ':fallback', {fallback: fallback});

          let fallback_label  = fallbacks_label + "::" +fallback.name;
          let fallback_hash   = fallback.checksum;
          let fallback_defect = false;

          if (
            me.defectFallbacks[currentChannel.channel_id]
            &&
            me.defectFallbacks[currentChannel.channel_id][fallback_label]
          ) {
            if (me.defectFallbacks[currentChannel.channel_id][fallback_label] >= me.maxSyncTry) {
              fallback_defect = true;
            }
          }

          if (
            isNullOrEmpty.isNullOrEmpty(playlist_to_sync)
            &&
            !keys.includes(fallback_label)
            &&
            !fallback_defect
          ) {
            playlist_to_sync        = fallback;
            playlist_to_sync_label  = fallback_label;
            playlist_to_sync_hash   = fallback_hash;

            me.logger.debug(me, log_label + ':playlist_to_sync', {playlist_to_sync_label: playlist_to_sync_label, playlist_to_sync: playlist_to_sync});
          }
        });

        if (!isNullOrEmpty.isNullOrEmpty(playlist_to_sync)) {
          me.loadPlaylist(currentChannel, playlist_to_sync.id, playlist_to_sync_label, playlist_to_sync_hash).then(() => {
            me.triggerUpdateEventPlaylist();

            me.isSyncingPlaylistsCount++;

            me.startPlaylistSync(force, currentChannel);

          }, (error) => {
            me.logger.error(me, log_label + ':loadPlaylist:error', {error: error, currentChannel: currentChannel, playlist_to_sync_label: playlist_to_sync_label});

            me.logger.debug(me, log_label + ':loadPlaylist:error:details', {defectPlaylists: me.defectPlaylists[currentChannel.channel_id]});
            if (!me.defectPlaylists[currentChannel.channel_id]) {
              me.defectPlaylists[currentChannel.channel_id] = {};
            }

            me.logger.debug(me, log_label + ':loadPlaylist:error:details', {playlist_to_sync_label: me.defectPlaylists[currentChannel.channel_id][playlist_to_sync_label]});
            if (!me.defectPlaylists[currentChannel.channel_id][playlist_to_sync_label]) {
              me.defectPlaylists[currentChannel.channel_id][playlist_to_sync_label] = 0;
            }

            me.storage.remove(playlist_to_sync_label).then(() => {
              me.defectPlaylists[currentChannel.channel_id][playlist_to_sync_label]++;

              me.logger.info(me, log_label + ':defectPlaylists', {
                defectPlaylists: me.defectPlaylists[currentChannel.channel_id],
                length: Object.keys(me.defectPlaylists[currentChannel.channel_id]).length
              });

              me.triggerUpdateEventPlaylist();

              me.startPlaylistSync(force, currentChannel);
            });
          });

        } else {
          me.logger.warn(me, log_label + ':complete', {
            autoStartTrackSync: me.autoStartTrackSync
          });

          me.isSyncingPlaylists = false;

          /*
          if (me.isSyncingPlaylistsCount > 0) {
            me.logger.info(me, log_label + ':complete:check:startTrackSync', {
              autoStartTrackSync: me.autoStartTrackSync,
              devMode: me.globalvarsProvider.devMode
            });

            if (me.autoStartTrackSync) {
              me.startTrackSync();

            } else {
              if (me.globalvarsProvider.devMode) me.askTrackSync();
            }

          } else {
            me.logger.info(me, log_label + ':complete:noChanges');
          }*/
/*
          // 2019-01-10 always start track sync
          if (me.autoStartTrackSync) {
            // SIA-20, 2019-02-03, currentChannel added
            me.startTrackSync(true, currentChannel);

          } else {
            if (me.globalvarsProvider.devMode) me.askTrackSync();
          }

          // 2019-02-12, TODO: not needed?
          //me.startAutoSync(true);
        }
      });

    }, error => {
      me.logger.error(me, log_label + ":updateChannelPlaylist:error", {error: error});

      me.isSyncingPlaylists = false;
    });
  }
*/

  updateChannelPlaylist(playlists_label: string, fallbacks_label: string, channel: any = null, force: boolean = false): Promise<any> {
    const me = this;
    let log_label: string = me.log_label_main + '::updateChannelPlaylist';

    me.logger.warn(me, log_label);

    let currentChannel: any = me.getCurrentChannel(channel);

    me.logger.info(me, log_label, {
      playlists_label: playlists_label,
      fallbacks_label: fallbacks_label,
      force: force
    });

    return new Promise((resolve, reject) => {

      let update: boolean = false;
      if (isNullOrEmpty.isNullOrEmpty(me.lastPlaylistUpdate[currentChannel.channel_id])) {
        // first run
        update = true;

      } else if (me.lastPlaylistUpdate[currentChannel.channel_id] < moment().add(-1 * me.syncInterval, "minutes")) {
        // too old
        update = true;
      }

      if (force) {
        // SIA-19, update view after click
        update = true;
      }

      me.logger.warn(me, log_label, {
        update: update,
        channel_id: channel.channel_id
      });

      if (!update) {
        // no update needed

        // playlists
        let playlists: object = {};
        me.playlists[currentChannel.channel_id].forEach((playlist) => {
          playlists[playlist.name] = playlist.checksum;
        });

        // fallbacks
        let fallbacks: object = {};
        me.fallbacks[currentChannel.channel_id].forEach((fallback) => {
          fallbacks[fallback.name] = fallback.checksum;
        });

        // SIA-31, 2019-02-19, verify
        me.verfiyPlaylists(playlists_label, playlists, fallbacks_label, fallbacks).then(() => {
          me.logger.info(me, log_label + ":verfiyPlaylists:done");

          resolve();

        }, error => {
          me.logger.error(me, log_label + ":verfiyPlaylists:error", {error: error});

          reject(error);
        });

      } else {
        // need update
        me.loadPlaylists(currentChannel).then(data => {
          // log
          me.logger.info(me, log_label + ":loadPlaylists", {
            data: data,
            dataType: typeof data,
            dataKeys: Object.keys(data),
            playlists: me.playlists[currentChannel.channel_id],
            fallbacks: me.fallbacks[currentChannel.channel_id],
          });

          // store
          me.getPlaylistsResult(data.data, data.fallback, data.meta.channel.id);

          // playlists
          let playlists: object = {};
          me.logger.info(me, log_label + ":playlists_type", {typeof: typeof data.data});

          data.data.forEach((playlist) => {
            playlists[playlist.name] = playlist.checksum;
          });
          me.logger.info(me, log_label + ":playlists", {playlists: playlists});

          // fallbacks
          let fallbacks: object = {};
          me.logger.info(me, log_label + ":fallbacks_type", {typeof: typeof data.fallback});

          data.fallback.forEach((fallback) => {
            fallbacks[fallback.name] = fallback.checksum;
          });
          me.logger.info(me, log_label + ":fallbacks", {fallbacks: fallbacks});

          // SIA-31, 2019-02-19, verify
          me.verfiyPlaylists(playlists_label, playlists, fallbacks_label, fallbacks).then(() => {
            me.logger.info(me, log_label + ":verfiyPlaylists:done");

            me.lastPlaylistUpdate[currentChannel.channel_id] = moment();

            me.triggerUpdateEventPlaylist();

            resolve();

          }, error => {
            me.logger.error(me, log_label + ":verfiyPlaylists:error", {error: error});

            reject(error);
          });

        }, error => {
          me.logger.error(me, log_label + ":loadPlaylists:error", {error: error});

          // 2018-04-09 TODO, resolving and rejecting
          me.dismissLoading().then(() => {
            me.logger.error(me, log_label + ":dismissLoading");

            reject(error);

          });
        });
      }
    });
  }

  private verfiyPlaylists(playlists_label: string, playlists: object, fallbacks_label: string, fallbacks: object): Promise<any> {
    const me = this;
    let log_label: string = me.log_label_main + '::verfiyPlaylists';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      playlists_label: playlists_label,
      playlists_length: Object.keys(playlists).length,
      fallbacks_label: fallbacks_label,
      fallbacks_length: Object.keys(fallbacks).length
    });
    me.logger.debug(me, log_label, {
      playlists: playlists,
      fallbacks: fallbacks
    });

    return new Promise((resolve, reject) => {
      // clean up
      let promises: Array<any> = [];

      // cycle storage - checksum check
      me.storage.keys().then(keys => {
        //console.log("syncService::updateChannelPlaylist:keys", typeof keys, keys);

        playlists_label = playlists_label + "::";
        fallbacks_label = fallbacks_label + "::";

        keys.forEach((key) => {
          // playlists
          if (key.includes(playlists_label)) {
            let pl: string = key.replace(playlists_label, "");
            me.logger.info(me, log_label + ":pl", {pl: pl});

            if (!playlists[pl]) {
              // pl does not exists => delete
              me.logger.info(me, log_label + ":pl:remove", {remove: pl});

              promises.push(me.storage.remove(key));

            } else {
              // pl exists => checksum check
              me.storage.get(key).then((data) => {
                let checksumPlaylist: any = me.createChecksumPlaylist(data);
                let checksum: string = CryptoJS.MD5(JSON.stringify(checksumPlaylist)).toString();

                if (checksum != playlists[pl]) {
                  me.logger.info(me, log_label + ":pl:remove:checksum", {pl: pl, checksum: checksum, playlist: playlists[pl]});

                  promises.push(me.storage.remove(key));
                }

              }, error => {
                reject(error);
              });
            }
          }

          // fallback
          if (key.includes(fallbacks_label)) {
            let fb: string = key.replace(fallbacks_label, "");
            me.logger.info(me, log_label + ":fb", {fb: fb});

            if (!fallbacks[fb]) {
              // pl does not exists => delete
              me.logger.info(me, log_label + ":fb:remove", {remove: fb});

              promises.push(me.storage.remove(key));

            } else {
              // pl exists => checksum check
              me.storage.get(key).then((data) => {
                let checksumPlaylist: any = me.createChecksumPlaylist(data);
                let checksum: string = CryptoJS.MD5(JSON.stringify(checksumPlaylist)).toString();

                if (checksum != fallbacks[fb]) {
                  me.logger.info(me, log_label + ":fb:remove:checksum", {fb: fb, checksum: checksum, fallback: fallbacks[fb]});

                  promises.push(me.storage.remove(key));
                }

              }, error => {
                reject(error);
              });
            }
          }
        });
      });

      Promise.all(promises).then(() => {
        me.logger.info(me, log_label + ":all:done");

        resolve();

      }, error => {
        me.logger.error(me, log_label + ":all:error", {error: error});

        reject(error);
      });
    });
  }

  playlistClearance(channel: any): void {
    const me = this;
    let log_label = me.log_label_main + '::playlistClearance';

    me.logger.warn(me, log_label);

    let currentChannel: any = me.getCurrentChannel(channel);

    let playlists_label: string = me.getPlaylistsLabel(currentChannel);
    let fallbacks_label: string = me.getFallbacksLabel(currentChannel);

    // run
    me.updateChannelPlaylist(playlists_label, fallbacks_label, channel, true)
      .then(() => {})
      .catch(() => {})
    ;

    /*
    let today: moment.Moment = moment().startOf('day');

    me.storage.keys().then(keys => {
      me.logger.debug(me, log_label + ':keys', {keys_type: typeof keys, keys: keys});

      keys.forEach((key) => {
        if (key.startsWith(playlists_label + "::pl_")) {
          let date: string = key.substr(key.length - 10);
          let pl_date: moment.Moment = moment(date);

          me.logger.debug(me, log_label, {
            today: today,
            date: date,
            pl_date: pl_date
          });

          if (pl_date < today) {
            me.storage.remove(key).then(() => {
              me.logger.debug(me, log_label + ':removed', {
                key: key,
                date: date
              });

            }, error => {
              me.logger.debug(me, log_label + ':remove:error', {
                key: key,
                date: date,
                error: error
              });
            });
          }
        }
      })
    });
    */
  }

/*
  askTrackSync(): void {
    const me = this;
    let log_label = me.log_label_main + '::askTrackSync';

    me.logger.warn(me, log_label);

    me.translateService.get(['sync_service_track_sync', 'sync_service_track_sync_message', 'sync_service_track_sync_ok', 'sync_service_track_sync_cancel']).subscribe(
      translation => {
        // value is our translated string
        me.logger.debug(me, log_label + ':translation', {translation: translation});

        let alert = me.alertCtrl.create({
          title: translation.sync_service_track_sync,
          message: translation.sync_service_track_sync_message,
          buttons: [{
            text: translation.sync_service_track_sync_cancel,
            role: 'cancel',
            handler: () => {
              me.logger.debug(me, log_label + ':click:cancel');
              me.autoCloseTrackSyncAlertCounterRun = false;
            }
          }, {
            text: translation.sync_service_track_sync_ok,
            handler: () => {
              me.logger.debug(me, log_label + ':click:ok');
              me.autoCloseTrackSyncAlertCounterRun = false;
              me.startTrackSync();
            }
          }]
        });
        alert.present();

        me.autoCloseTrackSyncAlertCounter = 0;
        me.autoCloseTrackSyncAlertCounterRun = true;
        me.autoCloseTrackSyncAlert(alert, translation.sync_service_track_sync);

      });
  }
*/

  autoCloseTrackSyncAlert(alert: any, title: string): void {
    const me = this;

    if (me.autoCloseTrackSyncAlertCounterRun) {
      if (me.autoCloseTrackSyncAlertCounter < me.autoCloseTrackSyncAlertCounterMax) {
        setTimeout(() => {
          //console.log("alert", alert);
          alert.data.title = title + " (" + (me.autoCloseTrackSyncAlertCounterMax-me.autoCloseTrackSyncAlertCounter) + ")";
          me.autoCloseTrackSyncAlertCounter++;
          me.autoCloseTrackSyncAlert(alert, title);
        }, 1000);

      } else {
        alert.dismiss();
      }
    }
  }

  getCurrentPlaylist(channel: Channel, set: boolean = true): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getCurrentPlaylist';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {channel_type: typeof channel, channel: channel});

    let current: any = {};

    return new Promise((resolve, reject) => {
      // translate
      me.translateService.get('loading_text_getcurrentplaylist').subscribe(
        value => {
          // loading screen
          if (set) me.showLoading(value);
        }
      );

      // check storage for todays playlist by channel and date
      let now: any = moment();
      //console.log("syncService::initiatePlaylist:now", now);

      if (me.globalvarsProvider.devMode && me.globalvarsProvider.offsetNowCalc) {
        now = moment().add(me.globalvarsProvider.offsetNow);
        me.logger.info(me, log_label + ":now-dev", {now: now});
      }

      let playlists_label: string               = "playlists_"+channel.channel_id;
      let fallbacks_label: string               = "fallbacks_"+channel.channel_id;

      let playlist_date: string                 = "pl_" + now.format("YYYY-MM-DD");
      let playlist_label: string                = playlists_label + "::" + playlist_date;
      let playlist_fallback: string             = "pl_fallback_" + now.format("ddd").toLowerCase();
      let playlist_fallback_label: string       = fallbacks_label + "::" + playlist_fallback;

      let playlist_date_next: string            = "pl_" + now.add(1, "day").format("YYYY-MM-DD");
      let playlist_label_next: string           = playlists_label + "::" + playlist_date_next;
      let playlist_fallback_next: string        = "pl_fallback_" + now.format("ddd").toLowerCase();
      let playlist_fallback_label_next: string  = fallbacks_label + "::" + playlist_fallback_next;

      let playlist_id: any                      = null;
      let playlist_id_next: any                 = null;

      let playlist_hash: string                 = null;
      let playlist_hash_next: string            = null;

      me.logger.info(me, log_label + ":playlists", {playlists_type: typeof me.playlists[channel.channel_id]});
      if (typeof me.playlists[channel.channel_id] == "undefined") {
        reject(new Error("playlists not ready yet"));
      }

      me.playlists[channel.channel_id].forEach(playlist => {
        //console.log("playlist", playlist);

        if (playlist.name == playlist_date) {
          if (set) {
            me.playlist              = playlist;
            me.playlist_date         = playlist_date;
          } else {
            current["playlist"]      = playlist;
            current["playlist_date"] = playlist_date;
          }
          playlist_id                = playlist.id;
          playlist_hash              = playlist.checksum;
        }

        if (playlist.name == playlist_date_next) {
          if (set) {
            me.playlist_next              = playlist;
            me.playlist_date_next         = playlist_date_next;
          } else {
            current["playlist_next"]      = playlist;
            current["playlist_date_next"] = playlist_date_next;
          }
          playlist_id_next                = playlist.id;
          playlist_hash_next              = playlist.checksum;
        }
      });

      me.logger.info(me, log_label + ":playlist_id", {playlist_id: playlist_id, nullOrEmpty: isNullOrEmpty.isNullOrEmpty(playlist_id)});
      me.logger.info(me, log_label + ":playlist_id_next", {playlist_id_next: playlist_id_next, nullOrEmpty: isNullOrEmpty.isNullOrEmpty(playlist_id_next)});

      /*if (isNullOrEmpty.isNullOrEmpty(playlist_id)) {
        reject("Playlist not found!");
      }*/

      let promises: Array<any> = [];

      promises.push(me.fetchPlaylist(playlist_label,
        playlist_date,
        playlist_id,
        playlist_hash,
        playlist_fallback_label,
        playlist_fallback,
        channel
      ).then(playlist => {
        if (set) {
          me.playlist              = playlist;
          me.playlist_date         = playlist_date;
        } else {
          current["playlist"]      = playlist;
          current["playlist_date"] = playlist_date;
        }
      }));

      promises.push(me.fetchPlaylist(playlist_label_next,
        playlist_date_next,
        playlist_id_next,
        playlist_hash_next,
        playlist_fallback_label_next,
        playlist_fallback_next,
        channel
      ).then(playlist => {
        if (set) {
          me.playlist_next              = playlist;
          me.playlist_date_next         = playlist_date_next;
        } else {
          current["playlist_next"]      = playlist;
          current["playlist_date_next"] = playlist_date_next;
        }
      }));

      Promise.all(promises).then(() => {
        me.logger.info(me, log_label + ":done", {loading: me.loading});

        // SIA-28, 2019-02-05, TODO: endless loop check
        //me.startPlaylistSync(true);
        // 2019-03-08
        //me.startAutoSync(false, true);

        if (set) {
          me.dismissLoading()
            .then(() => {
              resolve();
            })
            .catch(() => {
              resolve();
            });

        } else {
          resolve(current);
        }

      }, error => {
        me.logger.error(me, log_label + ":error", {error: error, loading: me.loading});

        if (set) {
          me.dismissLoading()
            .then(() => {
              reject(error);
            })
            .catch(() => {
              reject(error);
            });

        } else {
          reject(error);
        }

      });

      /*me.storage.get(playlist_label).then(playlist => {
        console.log('syncService::'+playlist_label+":got", playlist);

        if (!isNullOrEmpty.isNullOrEmpty(playlist)) {
          me.playlist = playlist;
          me.playlist_date = playlist_date;

          me.loading.dismiss().then( () => {
            me.startPlaylistSync(true);

            resolve();
          });

        } else {
          console.log('syncService::'+playlist_label+" empty or null");

          me.loadPlaylist(channel, playlist_id, playlist_label).then(data => {
            me.playlist = data;
            me.playlist_date = playlist_date;

            me.startPlaylistSync(true);

            resolve();
          }, error => {
            reject(error);
          });
        }

      }, error => {
        console.log('syncService::'+playlist_label+" not found");

        me.loadPlaylist(channel, playlist_id, playlist_label).then(data => {
          me.playlist = data;
          me.playlist_date = playlist_date;

          me.startPlaylistSync(true);

          resolve();
        }, error => {
          reject(error);
        });
      });*/
    });
  }

  fetchPlaylist(playlist_label: string,
                playlist_date: string,
                playlist_id: any,
                playlist_hash: string,
                playlist_fallback_label: string,
                playlist_fallback: string,
                channel: any
  ): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::fetchPlaylist';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      playlist_label: playlist_label,
      playlist_date: playlist_date,
      playlist_fallback_label: playlist_fallback_label,
      playlist_fallback: playlist_fallback
    }) ;

    let fallbackTest: boolean = false;
    if (me.globalvarsProvider.devMode && me.globalvarsProvider.playlistMissingTest) {
      fallbackTest = Math.random() >= 0.5;
      //fallbackTest = true;

      me.logger.info(me, log_label + ":fallbackTest", {
        playlist_label: playlist_label,
        fallbackTest: fallbackTest
      });
    }

    return new Promise(function(resolve, reject) {

      if (fallbackTest) {

        me.fetchFallback(playlist_fallback_label, null, null, null, channel).then(data => {
          me.switchToFallback(data, playlist_label, playlist_fallback_label).then(() => {
            resolve(data);
          });
        });

      } else {

        me.storage.get(playlist_label).then(playlist => {
          me.logger.debug(me, log_label + ":playlist", {
            playlist_label: playlist_label,
            playlist: playlist
          });

          if (!isNullOrEmpty.isNullOrEmpty(playlist)) {
            me.logger.debug(me, log_label + ":playlist:resolve", {
              playlist_label: playlist_label,
              playlist: playlist
            });

            resolve(playlist);

          } else {
            me.logger.debug(me, log_label + ":playlist:empty", {
              playlist_label: playlist_label,
              playlist: playlist
            });

            me.fetchPlaylistLoad(channel, playlist_id, playlist_label, playlist_hash, playlist_fallback_label).then(data => {
              me.logger.debug(me, log_label + ":playlist:empty:resolve", {
                playlist_label: playlist_label,
                playlist: data
              });

              resolve(data);

            }, error => {
              me.logger.error(me, log_label + ":playlist:empty:error", {error: error});

              reject(error);
            });
          }

        }, error => {
          me.logger.error(me, log_label + ":playlist:error", {error: error});

          me.fetchPlaylistLoad(channel, playlist_id, playlist_label, playlist_hash, playlist_fallback_label).then(data => {
            me.logger.debug(me, log_label + ":playlist:error:fetch:resolve", {
              playlist_label: playlist_label,
              playlist: data
            });

            resolve(data);

          }, error => {
            me.logger.error(me, log_label + ":playlist:error:fetch:error", {error: error});

            reject(error);
          });
        });

      }

    });
  }

  fetchPlaylistLoad(channel: any, playlist_id: any, playlist_label: string, playlist_hash: string, fallback_label: string): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::fetchPlaylistLoad';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      playlist_id: playlist_id,
      playlist_label: playlist_label,
      fallback_label: fallback_label
    });

    return new Promise(function(resolve, reject) {
      me.loadPlaylist(channel, playlist_id, playlist_label, playlist_hash).then(data => {
        me.logger.debug(me, log_label + ":loaded", {data: data});

        resolve(data);

      }, error => {
        me.logger.error(me, log_label + ":loaded:error", {error: error});

        me.fetchFallback(fallback_label, null, null, null, channel).then(data => {
          me.logger.debug(me, log_label + ":fallbacked", {data: data});

          me.switchToFallback(data, playlist_label, fallback_label).then(() => {
            me.logger.debug(me, log_label + ":fallbacked:switch", {data: data});

            resolve(data);

          }, error => {
            me.logger.error(me, log_label + ":fallbacked:switch:error", {error: error});

            reject(error);
          })

        }, error => {
          me.logger.error(me, log_label + ":fallbacked:error", {error: error});

          reject(error);
        });
      });
    });
  }

  fetchFallback(fallback_label: string, playlist_date: string, playlist_id: any, playlist_hash: string, channel: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::fetchFallback';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      fallback_label: fallback_label,
      playlist_date: playlist_date
    });

    return new Promise(function(resolve, reject) {

      me.storage.get(fallback_label).then(fallback => {
        me.logger.debug(me, log_label + ':fetchFallback', {
          fallback: fallback
        });

        if (!isNullOrEmpty.isNullOrEmpty(fallback)) {
          me.logger.debug(me, log_label + ":fetchFallback:resolve", {
            fallback_label: fallback_label,
            fallback: fallback
          });

          resolve(fallback);

        } else {
          me.logger.debug(me, log_label + ":fetchFallback:empty", {
            fallback_label: fallback_label,
            fallback: fallback
          });

          /*me.loadPlaylist(channel, playlist_id, fallback_label, playlist_hash).then(data => {
            resolve(data);

          }, error => {
            reject(error);
          });*/
        }

      }, error => {
        me.logger.error(me, log_label + ":fetchFallback:error", {
          error: error
        });

        /*me.loadPlaylist(channel, playlist_id, fallback_label, playlist_hash).then(data => {
          resolve(data);

        }, error => {
          reject(error);
        });*/

        reject(error);
      });
    });
  }

  switchToFallback(fallback_data: any, playlist_label: string, fallback_label: string): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::switchToFallback';

    me.logger.warn(me, log_label);

    let playlists: string = playlist_label.split("::")[0];
    let fallbacks: string = fallback_label.split("::")[0];

    let startdate: string = playlist_label.split("::")[1];
    startdate = startdate.replace("pl_", "");

    let start: any = moment(startdate);

    me.logger.info(me, log_label, {
      playlists: playlists,
      playlist_label: playlist_label,
      startdate: startdate,
      start: start,
      fallbacks: fallbacks,
      fallback_label: fallback_label
    });

    return new Promise((resolve, reject) => {

      // change startdate + add fallback property
      fallback_data.map(function(item) {
        let st: any = moment(item.starttime.gmt);
        let nst: any = start.clone();
        nst = nst.set({
          'hour':   st.get('hour'),
          'minute': st.get('minute'),
          'second': st.get('second')
        });

        let t: object = {
          gmt: nst.format("YYYY-MM-DD\THH:mm:ssZ"),
          unix: nst.unix()
        };

        me.logger.debug(me, log_label, {
          item: item,
          starttime: item.starttime,
          t: t
        });

        item.starttime = t;
        item.fallback = true;

        return item;
      });

      me.storage.set(playlist_label, fallback_data).then(() => {
        /*me.storage.get(playlists).then(playlists_data_item => {
          console.log("syncService::switchToFallback:playlists_data_item", playlists_data_item);

          if (playlists_data_item.name == playlist_label) {
            me.storage.get(fallbacks).then(fallbacks_data => {
              fallbacks_data.forEach(fallbacks_data_item => {
                console.log("syncService::switchToFallback:fallbacks_data_item", fallbacks_data_item);

                if (fallbacks_data_item.name == fallback_label) {
                  //me.storage
                }
              });

              resolve();

            }, error => {
              reject(error);
            });
          }

        }, error => {
          reject(error);
        });*/

        resolve();

      }, error => {
        reject(error);
      });
    });
  }

  createChecksumPlaylist(data: any): any {
    let t = [];

    data.forEach((row) => {
      let tt = {
        path: row.track_path, /* (row.track_path).replace(/\//g, "\\/"), */
        playtime: (row.playtime).toString(),
        artist: row.artist,
        title: row.title,
        hash: row.checksum,
        starttime: {
          unix: (row.starttime.unix).toString(),
          date: moment(row.starttime.gmt).format("YYYY-MM-DD HH:mm:ss")
        },
        weekday: row.weekday
      };

      t.push(tt);
    });

    return t;
  }

  loadPlaylist(channel: any, playlist_id: any, playlist_label: string, playlist_hash: string): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::loadPlaylist';

    return new Promise(function(resolve, reject) {
      me.apiProvider.callGetPlaylist(me.loading, channel, playlist_id, playlist_label, true).then(data => {
        let checksumPlaylist = me.createChecksumPlaylist(data);
        let checksum = CryptoJS.MD5(JSON.stringify(checksumPlaylist)).toString();

        me.logger.debug(me, log_label + ':callGetPlaylist', {
          data: data,
          checksumPlaylist: checksumPlaylist,
          checksum: checksum
        });

        /* debug
        me.json = JSON.stringify(checksumPlaylist);

        console.log("syncService::callGetPlaylist::data", data);
        console.log("syncService::callGetPlaylist::data:json", JSON.stringify(data));

        console.log("syncService::callGetPlaylist::checksumPlaylist", checksumPlaylist);
        console.log("syncService::callGetPlaylist::checksumPlaylist:json", JSON.stringify(checksumPlaylist));
        console.log("syncService::callGetPlaylist::checksumPlaylist:json.length", JSON.stringify(checksumPlaylist).length);

        console.log("syncService::callGetPlaylist::checksum", checksum);
        console.log("syncService::callGetPlaylist::playlist_hash", playlist_hash);
        */

        /* test */
        //checksum += "2";

        if (playlist_hash == checksum) {
          me.logger.debug(me, log_label + ':callGetPlaylist:resolve', {
            data: data
          });

          resolve(data);

        } else {
          let msg: string = "checksum mismatch: "+playlist_hash+" / "+checksum;

          me.logger.error(me, log_label + ':callGetPlaylist:checksum', {
            error: msg
          });

          reject(msg);
        }

      }, error => {
        me.logger.error(me, log_label + ':callGetPlaylist:error', {
          error: error
        });

        reject(error);
      });
    });
  }

  getCurrentTrack(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getCurrentTrack';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      current: me.current
    });

    return new Promise(function(resolve, reject) {
      // fallback test
      let fallbackTest: boolean = false;
      if (me.globalvarsProvider.devMode && me.globalvarsProvider.trackMissingTest) {
        //fallbackTest = Math.random() >= 0.5;
        fallbackTest = true;

        me.logger.debug(me, log_label, {
          fallbackTest: fallbackTest
        });
      }

      if (fallbackTest) {
        let msg: string = 'Track missing test';

        me.logger.error(me, log_label, {
          error: msg
        });

        reject(msg);
      }

      // process current track
      me.loadStoredTrack(me.channel, me.current).then((result) => {
        me.logger.debug(me, log_label + ':loadStoredTrack', {
          result: result
        });

        resolve(result);

      }, error => {
        me.logger.debug(me, log_label + ':loadStoredTrack:error', {
          error: error
        });

        // 2019-02-28, prevent checking for initial run
        let checkDefect: boolean = false;
        if (me.lastSync) {
          checkDefect = true;
        }

        me.logger.warn(me, log_label + ':loadStoredTrack:checkDefect', {
          lastSync: me.lastSync,
          checkDefect: checkDefect
        });

        me.loadTrack(me.channel, me.current, checkDefect).then((result) => {
          me.logger.debug(me, log_label + ':loadTrack', {
            result: result
          });

          me.triggerUpdateEventTrack();

          resolve(result);

        }, error => {
          me.logger.error(me, log_label + ':loadTrack:error', {
            error: error
          });

          reject(error);
        });
      });
    });
  }

  getFileMetadata(fullpath: string): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getFileMetadata';

    me.logger.info(me, log_label);
    me.logger.debug(me, log_label, {
      fullpath: fullpath
    });

    return new Promise((resolve, reject) => {
      me.file.resolveLocalFilesystemUrl(fullpath).then(entry => {
        me.logger.debug(me, log_label + ':resolveLocalFilesystemUrl', {
          fullpath: fullpath,
          entry: entry
        });

        entry.getMetadata(metadata => {
          me.logger.debug(me, log_label + ':getMetadata', {
            fullpath: fullpath,
            metadata: metadata
          });

          resolve(metadata);

        }, error => {
          me.logger.debug(me, log_label + ':getMetadata:error', {
            error: error
          });

          reject(error);
        });

      }, error => {
        me.logger.debug(me, log_label + ':resolveLocalFilesystemUrl:error', {
          error: error
        });

        reject(error);
      });
    });
  }

  getFileSize(dir: any, file: any, meta: boolean = false): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getFileSize';

    me.logger.info(me, log_label);

    let fullpath: string = dir.fullPath + file.name;

    me.logger.debug(me, log_label, {
      dir: dir,
      file: file,
      fullpath: fullpath
    });

    return new Promise((resolve, reject) => {
      me.getFileMetadata(fullpath).then(metadata => {
        me.logger.debug(me, log_label + ':getFileMetadata', {
          metadata: metadata
        });

        if (meta) {
          // meta
          resolve(metadata);

        } else {
          // size only
          resolve(metadata.size);
        }

      }, error => {
        me.logger.debug(me, log_label + ':getFileMetadata:error', {
          error: error
        });

        reject(error);
      })
    });
  }

  listDir(dir: any): Promise<any> {
    const me = this;

    return me.file.listDir(me.storageDirectory, dir);
  }

  getStorageDirectory(channel: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getStorageDirectory';

    me.logger.info(me, log_label);
    me.logger.debug(me, log_label, {
      channel: channel
    });

    let dir: string = "ch_" + channel.channel_id;

    let ret: object = {
      dir: dir,
      fullPath: me.storageDirectory + dir + "/"
    };

    return new Promise((resolve, reject) => {
      me.file.checkDir(me.storageDirectory, dir).then(exists => {
        me.logger.debug(me, log_label + ':checkDir', {
          exists: exists
        });

        resolve(ret);

      }, error => {
        me.logger.error(me, log_label + ':checkDir:error', {
          error: error
        });

        me.file.createDir(me.storageDirectory, dir, false).then(entry => {
          me.logger.debug(me, log_label + ':createDir', {
            entry: entry
          });

          resolve(ret);

        }, error => {
          me.logger.error(me, log_label + ':createDir:error', {
            error: error
          });

          reject(error);
        });
      });
    });
  }

  loadTrack(channel: any, track: any, checkDefect: boolean = false): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::loadTrack';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      track: track,
      channel: channel
    });

    return new Promise((resolve, reject) => {
      let fallback: boolean = false;
      if (track.fallback) {
        fallback = true;
      }

      // 2019-03-13
      let fileName = me.getFileName(track);
      me.logger.warn(me, log_label, {fileName: fileName});

      /* https://forum.ionicframework.com/t/filetransfer-plugin-do-not-work-on-browser/109738 */

      me.getStorageDirectory(channel).then(dir => {
        let storageDirectory = dir.fullPath;

        //me.file.checkFile(me.file.dataDirectory, fileName).then(exists => {
        me.file.checkFile(storageDirectory, fileName).then(exists => {
          me.logger.info(me, log_label + ":checkFile", {exists: exists});

          //me.file.resolveDirectoryUrl(me.file.dataDirectory).then(dirEntry => {
          me.file.resolveDirectoryUrl(storageDirectory).then(dirEntry => {
            me.file.getFile(dirEntry, fileName, {create: false}).then(entry => {
              me.logger.debug(me, log_label + ":getfile", {entry: entry});

              let tracks_label: string = me.getTracksLabel(channel);

              me.storage.get(tracks_label).then(tracks => {

                if (
                  !isNullOrEmpty.isNullOrEmpty(tracks)
                  &&
                  tracks[track.track_id]
                ) {

                  let data: object = tracks[track.track_id].data;

                  me.storeLoadedTrack(channel, track, data, entry).then(result => {
                    me.logger.debug(me, log_label + ":storeLoadedTrack:complete", {result: result});

                    resolve(result);

                  }, error => {
                    // handle error
                    me.logger.error(me, log_label + ":storeLoadedTrack:error", {error: error});

                    reject(error);
                  });

                } else {
                  // handle error
                  let error = new Error("track (" + track.track_id + ") not found in tracks (" + tracks_label +")");
                  me.logger.debug(me, log_label + ":tracks:error", {error: error});

                  // 2019-03-05
                  me.loadTrackCheckDownload(checkDefect, channel, track, fallback, storageDirectory, fileName, entry).then(result => {
                    resolve(result);

                  }, error => {
                    // handle error
                    me.logger.error(me, log_label + ":loadTrackCheckDownload:error", {error: error});

                    reject(error);
                  });
                }

              }, error => {
                // handle error
                me.logger.debug(me, log_label + ":trackInfo:error", {error: error});

                reject(error);
              });

            }, error => {
              // handle error
              me.logger.error(me, log_label + ":getFile:error", {error: error});

              reject(error);
            });

          }, error => {
            // handle error
            me.logger.error(me, log_label + ":resolveDirectoryUrl:error", {error: error});

            reject(error);
          });

        }, error => {
          // handle error

          // 2019-01-27
          if (error.message == "NOT_FOUND_ERR") {
            // default not found behaviour
            me.logger.debug(me, log_label + ":checkFile:error", {error: error});

          } else {
            // other reason
            me.logger.error(me, log_label + ":checkFile:error", {error: error});
          }

          // 2019-03-05
          me.loadTrackCheckDownload(checkDefect, channel, track, fallback, storageDirectory, fileName).then(result => {
            resolve(result);

          }, error => {
            // handle error
            me.logger.error(me, log_label + ":loadTrackCheckDownload:error", {error: error});

            reject(error);
          });
        });

      }, error => {
        // handle error
        me.logger.error(me, log_label + ":getStorageDirectory:error", {error: error});

        reject(error);
      });

    });
  }

  loadTrackCheckDownload(checkDefect: boolean, channel: any, track: any, fallback: boolean, storageDirectory: string, fileName: string, entry: any = null): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::loadTrackCheckDownload';

    me.logger.warn(me, log_label);

    return new Promise((resolve, reject) => {
      me.logger.warn(me, log_label + ":checkDefect", {
        checkDefect: checkDefect,
        includes: me.defectTracks.includes(track.track_id)
      });

      // 2019-02-27, prevent defect track downloads
      if (checkDefect && me.defectTracks.includes(track.track_id)) {
        let error: string = "track " + track.track_path + " is marked defect";

        me.downloadTry = me.maxDownloadTry;

        me.logger.error(me, log_label + ":loadTrackDownload:error", {error: error, track: track});

        reject(error);

      } else {
        // continue to download
        me.loadTrackDownload(channel, track, fallback, storageDirectory, fileName, entry).then(result => {
          resolve(result);

        }, error => {
          // handle error
          me.logger.error(me, log_label + ":loadTrackDownload:error", {error: error});

          reject(error);
        });
      }
    });
  }

  loadTrackDownload(channel: any, track: any, fallback: boolean, storageDirectory: string, fileName: string, entry: any = null): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::loadTrackDownload';

    me.logger.warn(me, log_label);
    me.logger.warn(me, log_label, {
      track: track,
      channel: channel,
      fallback: fallback,
      storageDirectory: storageDirectory,
      fileName: fileName,
      entry: entry
    });

    return new Promise((resolve, reject) => {
      // 2019-03-22
      me.apiProvider.callGetSecureDownload(channel, track.id, true, fallback).then(data => {
      //me.apiProvider.callGetSecureDownload(channel, track.track_id, true, false, true).then(data => {
        me.logger.debug(me, log_label + ":callGetSecureDownload:data", {data: data});

        if (isNullOrEmpty.isNullOrEmpty(entry)) {
          // need download
          me.logger.info(me, log_label + ":checkFile:startDownload", {url: data.url});

          let percent: number = 5;
          let lastPercent: number = 0;
          me.fileTransfer.onProgress((progress) => {
            let current: number = Math.floor(progress.loaded / progress.total * 100);

            if (current % percent == 0 && current > lastPercent) {
              me.logger.debug(me, log_label + ":download:progress", {current: current});

              // 2019-03-22, toast
              if (me.isSyncingTracks) {
                if (me.loopTracks.length == 1) {
                  me.presentToast(me.toastMessages['toast_sync_download_track'].replace("{{x}}", 1).replace("{{dl}}", "(" + current + " %)"));

                } else {
                  me.presentToast(me.toastMessages['toast_sync_download_tracks'].replace("{{x}}", me.loopTracks.length).replace("{{dl}}", "(" + current + " %)"));
                }
              }

              lastPercent = current;
            }
          });

          me.fileTransfer.download(data.url, storageDirectory + fileName).then((entry) => {
            //me.fileTransfer.download(data.url, me.file.tempDirectory + fileName).then((entry) => {
            me.logger.info(me, log_label + ":download:complete", {url: entry.toURL(), entry: entry});

            me.storeLoadedTrack(channel, track, data, entry).then(result => {
              me.logger.debug(me, log_label + ":storeLoadedTrack", {result: result});

              resolve(result);

            }, error => {
              // handle error
              me.logger.error(me, log_label + ":storeLoadedTrack:error", {error: error});

              reject(error);
            });

          }, (error) => {
            // handle error
            me.logger.error(me, log_label + ":download:error", {error: error});

            reject(error);
          });

        } else {
          // uses existing file
          me.storeLoadedTrack(channel, track, data, entry).then(result => {
            me.logger.debug(me, log_label + ":storeLoadedTrack", {result: result});

            resolve(result);

          }, error => {
            // handle error
            me.logger.error(me, log_label + ":storeLoadedTrack:error", {error: error});

            reject(error);
          });
        }

      }, error => {
        // handle error
        me.logger.error(me, log_label + ":callGetSecureDownload:error", {error: error});

        reject(error);
      });
    });
  }

  loadStoredTrack(channel: any, track: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::loadStoredTrack';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {channel_type: typeof channel, channel: channel, track: track});

    return new Promise((resolve, reject) => {
      let tracks_label: string = "tracks_" + channel.channel_id;
      me.logger.warn(me, log_label + ":tracks_label", {tracks_label: tracks_label});

      me.storage.get(tracks_label).then(tracks => {
        let tracksEmpty: boolean = isNullOrEmpty.isNullOrEmpty(tracks);

        me.logger.warn(me, log_label + ":tracks", {
          id: track.track_id,
          length: (tracksEmpty) ? 0 : Object.keys(tracks).length,
          defectTracks: me.defectTracks,
          skipDownloadTracks: me.skipDownloadTracks
        });
        me.logger.debug(me, log_label + ":tracks", {
          tracks: tracks
        });

        if (tracksEmpty) {
          let error: string = "tracks " + tracks_label + " empty";

          me.logger.error(me, log_label + ":tracks:error", {error: error});

          reject(error);

        } else {
          if (tracks[track.track_id]) {
            // json normally not needed anymore
            // TODO: track stopping android
            me.logger.warn(me, log_label + ":track:found", {
              track_type: typeof tracks[track.track_id],
              track: tracks[track.track_id]
            });

            if (typeof tracks[track.track_id] == "string") {
              resolve(JSON.parse(tracks[track.track_id]));

            } else {
              resolve(tracks[track.track_id]);
            }

          } else {
            let error: string = "track " + track.track_path + " not found";

            me.logger.error(me, log_label + ":tracks:error", {error: error});

            reject(error);
          }
        }

      }, error => {
        me.logger.error(me, log_label + ":tracks:error", {error: error});

        reject(error);
      });
    });
  }

  storeLoadedTrack(channel: any, track: any, data: any, entry: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::storeLoadedTrack';

    me.logger.warn(me, log_label);
    me.logger.warn(me, log_label, {
      channel: channel,
      track: track,
      data: data,
      entry: entry
    });

    me.logger.debug(me, log_label + ":track", {
      data_url: data.url,
      entry_nativeURL: entry.nativeURL
    });

    return new Promise((resolve, reject) => {

      entry.file(file => {
        me.logger.debug(me, log_label + ":file", {
          file: file,
          storageDirectory: me.storageDirectory
        });

        // download complete and size matches
        if (data.size != file.size) {
          me.logger.info(me, log_label + ":filesize:mismatch", {
            data: data.size,
            file: file.size
          });

          let error = new Error("file size mismatch (" + data.size + " / " + file.size + ")");

          me.logger.error(me, log_label + ":filesize:mismatch:error", {
            error: error,
            entry: entry,
            file: file
          });

          let removePath: string = entry.nativeURL.replace(entry.name, '');
          me.file.removeFile(removePath, entry.name).then(() => {
            reject(error);

          }, error2 => {
            me.logger.error(me, log_label + ":file:removeFile", {
              error: error,
              error2: error2,
              removePath: removePath,
              filename: entry.name
            });

            reject(error);
          });

        } else {
          me.logger.debug(me, log_label + ":filesize", {
            data: data.size,
            file: file.size
          });

          // check checksum
          me.getStorageDirectory(channel).then((dir) => {
            me.logger.debug(me, log_label + ":dir", {
              dir_fullpath: dir.fullPath,
              file_name: file.name
            });

            me.logger.info(me, log_label + ":checksum:start");

            let startTime: any = moment();
            me.getMD5FileHash(entry, dir, file, startTime).then((hash) => {
              let md5: string = hash.md5;
              let duration: any = hash.duration;

              if (md5 != track.checksum) {
                me.logger.info(me, log_label + ":checksum:mismatch", {
                  duration: duration.asSeconds(),
                  md5: md5,
                  checksum: track.checksum
                });

                let error = new Error("file checksum mismatch (" + md5 + " / " + track.checksum + ")");

                me.logger.error(me, log_label + ":checksum:mismatch:error", {error: error});

                reject(error);

              } else {
                me.logger.debug(me, log_label + ":checksum", {
                  duration: duration.asSeconds(),
                  md5: md5,
                  checksum: track.checksum
                });

                // file size and checksum correct
                let tracks_label: string = me.getTracksLabel(channel);
                me.logger.info(me, log_label + ":checksum:tracks_label", tracks_label);

                me.storage.get(tracks_label).then(tracks => {
                  //console.log("tracks", tracks);

                  if (isNullOrEmpty.isNullOrEmpty(tracks)) {
                    tracks = {};
                  }

                  // strip not used data from result
                  delete data.url;
                  data.size = parseInt(data.size);

                  delete entry.isDirectory;
                  delete entry.isFile;
                  delete entry.name;
                  delete entry.filesystem;    // 2018-04-24

                  delete file.end;
                  delete file.lastModified;
                  delete file.lastModifiedDate;
                  delete file.start;
                  delete file.type;
                  delete file.name;
                  delete file.size;

                  let result: object = {
                    data: data,
                    entry: entry,
                    file: file
                  };
                  me.logger.debug(me, log_label + ":result", {result: result});

                  //let json_data: string = JSON.stringify(result);
                  //tracks[track.track_id] = json_data;
                  tracks[track.track_id] = result;

                  //if (debug_detail) console.log(log_label + ":json_size", json_data.length);

                  me.storage.set(tracks_label, tracks).then(() => {
                    me.logger.warn(me, log_label + ":storage:set:done");

                    me.unmarkDefectTrack(track);

                    resolve(result);

                  }, error => {
                    me.logger.error(me, log_label + ":storage:set:error", {error: error});

                    reject(error);
                  })

                }, error => {
                  me.logger.error(me, log_label + ":storage:get:error", {error: error});

                  reject(error);
                });
              }

            }, (error) => {
              me.logger.error(me, log_label + ":getMD5FileHash:error", {error: error});

              reject(error);
            });

          }, error => {
            me.logger.error(me, log_label + ":getStorageDirectory:error", {error: error});

            reject(error);
          });
        }

      }, error => {
        me.logger.error(me, log_label + ":file:error", {error: error});

        reject(error);
      });
    });
  }

  private getMD5FileHash(entry: any, dir: any, file: any, startTime: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getMD5FileHash';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      entry: entry,
      dir: dir,
      file: file,
      startTime: startTime,
      ios: me.platform.is('ios'),
      android: me.platform.is('android')
    });

    if (me.platform.is('ios') || me.platform.is('android')) {
      return me.readMD5FileHashMobile(entry, startTime);

    } else {
      return me.readMD5FileHashCompat(dir, file, startTime);
    }
  }

  private readMD5FileHashMobile(entry: any, startTime: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::readMD5FileHashMobile';

    //me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      entry: entry
    });

    return new Promise((resolve, reject) => {
      window['md5chksum'].file(entry, (md5sum) => {
        let duration: any = moment.duration(moment().diff(startTime));

        me.logger.debug(me, log_label + ":md5chksum:done", {
          md5: md5sum,
          duration: duration.asSeconds()
        });

        resolve({md5: md5sum, duration: duration});

      }, (error) => {
        me.logger.error(me, log_label + ":md5chksum:error", {error: error});

        reject(error);
      })
    });
  }

  private readMD5FileHashCompat(dir: any, file: any, startTime: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::readMD5FileHashCompat';

    //me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      fullpath: dir.fullPath,
      filename: file.name
    });

    return new Promise((resolve, reject) => {

      me.file.readAsBinaryString(dir.fullPath, file.name).then((content) => {
        let text: string = CryptoJS.enc.Latin1.parse(content);
        let md5: string = CryptoJS.MD5(text).toString();

        let duration: any = moment.duration(moment().diff(startTime));

        me.logger.debug(me, log_label + ":readAsBinaryString:done", {
          md5: md5,
          duration: duration.asSeconds()
        });

        resolve({md5: md5, duration: duration});

      }, (error) => {
        me.logger.error(me, log_label + ":readAsBinaryString:error", {error: error});

        reject(error);
      });
    });
  }

  getFreeSpace(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getFreeSpace';

    me.logger.warn(me, log_label);

    return new Promise(resolve => {
      me.file.getFreeDiskSpace().then(size => {
        // 2019-01-09 me.zone.run(() => {

        // TODO: iOS bugfix
        if (me.platform.is('ios')) {
          size = size / 1024; // to kb
        }

        // now in sync page
        //size = size / 1024; // to mb
        //size = size / 1024; // to gb

        me.storageSize = size;

        // send event to sync page
        me.triggerUpdateEventStorageSize();

        resolve();

      }, error => {
        me.logger.error(me, log_label +':error', error);

        me.storageSize = 0;

        // send event to sync page
        me.triggerUpdateEventStorageSize();

        resolve();
      });
    });
  }

  private switchPlayer(): void {
    const me = this;
    let log_label = me.log_label_main + '::switchPlayer';

    me.logger.warn(me, log_label);

    if (me.playing) {
      me.doPause();
    }

    me.tryGetCurrentTrack();
  }

  checkPlayer(): void {
    const me = this;
    let log_label = me.log_label_main + '::checkPlayer';

    me.logger.warn(me, log_label, {
      playing: me.playing,
      trackPlaying: me.trackPlaying,
      mediaObject: me.mediaObject
    });

    if (!me.playing && me.trackPlaying) {

    }
  }

  playTrack(track: any): void {
    /*
     https://www.w3schools.com/tags/av_prop_currenttime.asp
     https://ionicframework.com/docs/native/media/
     https://github.com/apache/cordova-plugin-file
     https://forum.ionicframework.com/t/ios-9-cant-play-audio-video-files-that-are-downloaded-to-device/37580/2
     */

    const me = this;
    let log_label = me.log_label_main + '::playTrack';

    me.logger.warn(me, log_label, {
      track: track
    });
    me.logger.info(me, log_label, {
      track: track,
      played: this.current.played,
      interrupt: this.interrupt
    });

    /*me.file.resolveLocalFilesystemUrl(track).then(dir => {
      let basePath = dir.toInternalURL();
      console.log("playTrack::basePath", basePath);
    });*/

    me.logger.info(me, log_label + ":mediaObject", {
      mediaObject_type: typeof me.mediaObject,
      mediaObject: me.mediaObject,
      chromsecastsession_type: typeof me.chromecastSession,
      chromcastsession_check: isNullOrEmpty(me.chromecastSession),
      chromecastsession: me.chromecastSession
    });

    //if (typeof me.chromecastSession !== "undefined") {
    if (!isNullOrEmpty(me.chromecastSession)) {
      // chromecast
      me.playTrackChromecast(track);

    } else {
      // device via mediaobject

      if (typeof me.mediaObject !== "undefined") {
        me.logger.warn(me, log_label + ":mediaObject:release");

        me.interrupt = true;

        me.clearTrackPlayer();

        me.mediaObject.release();
      }

      me.mediaObject = me.media.create(track);

      if (me.mediaObject && me.mediaObject.onStatusUpdate) {
        me.mediaObject.onStatusUpdate.subscribe(status => {
          me.logger.warn(me, log_label + ":status", {
            status: status,
            interrupt: me.interrupt
          });
          me.logger.warn(me, log_label + ":status:current", {
            played: me.current.played,
            duration: me.current.duration
          });

          if (status == 2) {
            // play
            if (
              me.current.played > 0
              // 2019-03-05
              &&
              me.current.played < (me.current.duration - me.seekSafetyTime)
            ) {
              me.logger.warn(me, log_label + ":seek", {seek: me.current.played});
              me.mediaObject.seekTo(me.current.played * 1000);
            }

            // 2018-11-05
            if (me.lastVolume) {
              me.logger.warn(me, log_label + ":volume", {volume: me.lastVolume});
              me.mediaObject.setVolume(me.lastVolume);
            }

            me.createMusicControls();

            me.zone.run(() => {
              me.playing = true;
            });

            me.trackPlaying = true;

            me.trackPlayer(true);

            me.interrupt = false;

            // 2018-04-09 TODO
            me.dismissLoading()
              .then(() => {})
              .catch(() => {})
            ;

            // 2019-03-22, TODO
            if (me.usePauseSync) me.unpauseSync();
          }

          if (status == 3) {
            // pause
            me.clearTrackPlayer();

            me.zone.run(() => {
              me.playing = false;
            });

            me.trackPlaying = false;
          }

          if (status == 4) {
            // end
            me.last = me.current;

            if (me.interrupt) {
              me.logger.warn(me, log_label + ":trackEnded:interrupted");
              me.interrupt = false;

            } else {
              me.logger.warn(me, log_label + ":trackEnded:next");

              //me.clearTrackPlayer();

              me.zone.run(() => {
                me.playing = false;
              });

              me.trackPlaying = false;

              // 2019-03-22
              if (me.usePauseSync) me.preparePauseSync();

              me.initiatePlaylist(true);
            }
          }
        }); // fires when file status changes

        me.mediaObject.onSuccess.subscribe(() => {
          me.logger.info(me, log_label + ":success");
        });

        me.mediaObject.onError.subscribe(error => {
          me.logger.error(me, log_label + ":error", {error: error});

          me.clearTrackPlayer();

          me.zone.run(() => {
            me.playing = false;
          });

          me.trackPlaying = false;

          // 2019-03-22
          if (me.usePauseSync) me.pauseSync();

          me.logger.info(me, log_label + ":error", {channel: me.channel, track: me.current});

          me.deleteStoredTrack(me.channel, me.current).then(() => {
            me.tryGetCurrentTrack();

          }, () => {
            me.tryGetCurrentTrack();
          });
        });

        me.mediaObject.play();

      } else {
        me.logger.error(me, log_label + ":onStatusUpdate:error", {mediaObject: me.mediaObject});
      }
    }
  }

  checkFirstPlay(played: number, duration: number): void {
    const me = this;
    let log_label = me.log_label_main + '::checkFirstPlay';

    let border: number = Math.ceil(duration * 2 / 3);

    me.logger.warn(me, log_label, {
      played: played,
      duration: duration,
      border: border,
      firstPlay: me.firstPlay
    });

    if (
      !me.firstPlay
      // 2019-03-22, check played for beginning of track (2/3)
      &&
      played < border
    ) {
      me.firstPlay = true;

      me.logger.warn(me, log_label, {
        firstPlay: me.firstPlay
      });
    }
  }

  resetFirstPlay(): void {
    const me = this;
    let log_label = me.log_label_main + '::resetFirstPlay';

    me.logger.warn(me, log_label);

    me.firstPlay = false;
  }

  playTrackChromecast(track: any): void {
    const me = this;
    let log_label = me.log_label_main + '::playTrackChromecast';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      track: track,
      played: me.current.played,
      interrupt: me.interrupt
    });

    me.file.resolveLocalFilesystemUrl(track).then(entry => {
      me.logger.info(me, log_label, {entry: entry});

      //let mediaInfo = new cordova.plugins.chromecast.media.MediaInfo(entry.nativeURL, "audio/mpeg");
      let mediaInfo = new cordova.plugins.chromecast.media.MediaInfo("http://192.168.0.21:8012" + entry.fullPath, "audio/mpeg");
      mediaInfo.metadata = new cordova.plugins.chromecast.media.MusicTrackMediaMetadata();

      let request = new cordova.plugins.chromecast.media.LoadRequest(mediaInfo);

      me.logger.info(me, log_label, {
        mediaInfo: mediaInfo,
        request: request
      });

      let onSessionUpdate = function(isAlive, session) {
        me.logger.info(me, log_label + ":onSessionUpdate", {
          isAlive: isAlive,
          session: session
        });
      };

      let onMediaDiscovered = function(media) {
        me.logger.info(me, log_label + ":onMediaDiscovered", {
          media: media
        });

        media.addUpdateListener((isAlive, media) => {
          me.logger.info(me, log_label + ":onMediaUpdate", {
            isAlive: isAlive,
            media: media
          });

          if (media.currentTime == 0 && media.playerState == "PLAYING") {
            // play
            if (me.current.played > 0) {
              me.logger.info(me, log_label + ":seek", {seek: me.current.played});
              media.seek({currentTime: me.current.played}, (result) => {
                me.logger.info(me, log_label + ":seek:result", {result: result});

              }, (error) => {
                me.logger.error(me, log_label + ":seek:error", {error: error});
              });
            }

            // 2018-11-05
            if (me.lastVolume) {
              me.logger.info(me, log_label + ":volume", {volume: me.lastVolume});
              media.setVolume({volume: {level: me.lastVolume}}, (result) => {
                me.logger.info(me, log_label + ":volume:result", {result: result});

              }, (error) => {
                me.logger.error(me, log_label + ":volume:error", {error: error});
              });
            }

            //me.createMusicControls();

            me.zone.run(() => {
              me.playing = true;
            });

            //me.trackPlaying = true;

            //me.trackPlayer();

            me.interrupt = false;

            // 2018-04-09 TODO
            me.dismissLoading();

          } else if (media.idleReason == "finished") {
            // stop
            //me.clearTrackPlayer();

            me.zone.run(() => {
              me.playing = false;
            });

            me.trackPlaying = false;

            me.initiatePlaylist(true);
          }
        });

        me.globalvarsProvider.getChromeCast().addUpdateListener(onSessionUpdate);
      };

      let onMediaError = function(e) {
        me.logger.error(me, log_label + ":onMediaError", {error: e});
      };

      me.chromecastSession.loadMedia(request,
        //onMediaDiscovered.bind(this, 'loadMedia'),
        onMediaDiscovered,
        onMediaError);
    });

  }

  clearTrackPlayer(): void {
    const me = this;
    let log_label = me.log_label_main + '::clearTrackPlayer';

    me.logger.debug(me, log_label);

    if (me.trackingLoop) {
      me.logger.debug(me, log_label + ":clear");
      clearTimeout(me.trackingLoop);
    }
  }

  trackPlayer(first: boolean = false): void {
    const me = this;
    let log_label = me.log_label_main + '::trackPlayer';

    me.logger.debug(me, log_label, {first: first});

    me.clearTrackPlayer();

    const timeout: number = 1000;
    me.trackingLoop = setTimeout( () => {

      me.mediaObject.getCurrentPosition().then((position) => {
        me.logger.debug(me, log_label, {position: position, trackPlaying: me.trackPlaying});

        me.zone.run(() => {
          me.current.played = position;
        });

        if (first) {
          // 2019-03-08
          me.checkFirstPlay(me.current.played, me.current.duration);
        }

        if (me.trackPlaying) me.trackPlayer()

      }, error => {
        me.logger.error(me, log_label, {error: error, trackPlaying: me.trackPlaying});
      });

    }, timeout);
  }

  initiatePlaylist(track_change: boolean = false): void {
    const me = this;
    let log_label = me.log_label_main + '::initiatePlaylist';

    me.logger.warn(me, log_label, {
      track_change: track_change,
      downloadTacks: me.trackDownloadList.length,
      defectTracks: me.defectTracks.length,
      skipDownloadTracks: me.skipDownloadTracks.length
    });
    me.logger.info(me, log_label, {
      track_change: track_change,
      playlist_date: this.playlist_date,
      playlist: this.playlist
    });

    let playlist_date: string = "pl_" + moment().format("YYYY-MM-DD");
    if (me.globalvarsProvider.devMode && me.globalvarsProvider.offsetNowCalc) {
      playlist_date = "pl_" + moment().add(me.globalvarsProvider.offsetNow).format("YYYY-MM-DD");
    }

    me.logger.info(me, log_label + ":playlist_date", {playlist_date: playlist_date, playlist_date_stored: me.playlist_date});
    if (me.playlist_date != playlist_date) {
      me.getCurrentPlaylist(me.channel).then(() => {
        me.initiatePlaylist(true);

      }, (error) => {
        me.logger.error(me, log_label + ":error", {error: error});
      });

      return;
    }

    // translate
    me.translateService.get('loading_text_getcurrentplaytrack').subscribe(
      value => {
        // loading screen
        me.showLoading(value);
      }
    );

    // track determination
    me.getCurrentPlaylistTracks(track_change, me.playlist, me.playlist_date, me.playlist_next, me.playlist_date_next).then((data) => {
      me.current = data.current;
      me.next = data.next;

      me.logger.info(me, log_label + ":played", {
        played: me.current.played,
        duration: me.current.duration
      });

      me.downloadTry = 0;
      me.tryGetCurrentTrack();

    });
  }

  getCurrentPlaylistTracks(track_change: boolean, playlist: any, playlist_date: string, playlist_next: any, playlist_date_next: string): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getCurrentPlaylistTracks';

    me.logger.warn(me, log_label);

    return new Promise((resolve) => {

      let current: any = null;
      let first_item: any = null;
      let last_item: any = null;
      let next_max: number = 10;
      let next: Array<any> = [];
      let i: number = 0;

      let now: any = moment().unix();
      //console.log("syncService::initiatePlaylist:now", now);

      if (me.globalvarsProvider.devMode && me.globalvarsProvider.offsetNowCalc) {
        now = moment().add(me.globalvarsProvider.offsetNow).unix();
        me.logger.info(me, log_label + ":now-dev", {now: now});
      }

      let correctiv: number = 0;
      let diff: number = 0;

      playlist.forEach(item => {
        me.logger.debug(me, log_label + ":item", {item: item});

        // calc correctiv
        if (last_item) {
          diff = last_item.starttime.unix + last_item.playtime;
          if (diff > 0 && diff < 1) {
            correctiv = diff;
          } else {
            correctiv = 0;
          }
          me.logger.debug(me, log_label + ":last_item", {
            unix: last_item.starttime.unix,
            playtime:  last_item.playtime,
            calc: (item.starttime.unix - diff),
            diff: diff
          });
        }

        if (item.starttime.unix - correctiv <= now) {
          // set last item
          last_item = item;

        } else if (i < next_max) {
          // add next
          next.push(item);
          i++;
        }

        // 2019-02-28, first item for backup if last item empty
        if (isNullOrEmpty(first_item)) {
          first_item = item;
        }
      });

      me.logger.warn(me, log_label + ":last_item", {
        last_item: last_item
      });

      if (last_item) {
        me.logger.debug(me, log_label + ":now", {
          now: now,
          calc: (now - last_item.starttime.unix)
        });
      }

      me.logger.info(me, log_label + ":next", {
        length: next.length,
        max: next_max,
        next: next
      });

      if (next.length < next_max) {
        me.logger.info(me, log_label + ":next:shortage", {
          playlist_date: playlist_date,
          playlist: playlist
        });

        i = next.length;
        playlist_next.forEach(item => {
          if (i < next_max) {
            // add next
            next.push(item);
            i++;
          }
        });

        me.logger.info(me, log_label + ":next:shortage", {
          playlist_date: playlist_date_next,
          playlist: playlist_next
        });
      }

      /*let played = 0;
      if (me.current && me.current.played)
        played = me.current.played;*/

      now = moment().unix();
      me.logger.debug(me, log_label + ":now", {now: now});

      if (me.globalvarsProvider.devMode && me.globalvarsProvider.offsetNowCalc) {
        now = moment().add(me.globalvarsProvider.offsetNow).unix();
        me.logger.info(me, log_label + ":now-dev", {now: now});
      }

      // 2019-02-28, first item for backup if last item empty
      if (isNullOrEmpty(last_item)) {
        me.logger.error(me, log_label + ":last_item:empty", {
          last_item: last_item,
          first_item: first_item
        });

        last_item = first_item;
      }

      current = last_item;
      if (track_change) {
        me.current.played = 0;

        // SIA-18, reset music controller on track change
        // 2019-01-29, deactivated due to stopping the background playing
        //me.resetMusicControls();

      } else {
        if (last_item) {
          current.played = now - last_item.starttime.unix;
        } else {
          current.played = now;
        }
      }
      current.duration = last_item.playtime;

      resolve({
        current: current,
        next: next
      })
    });
  }

  tryGetCurrentTrack(): void {
    const me = this;
    let log_label = me.log_label_main + '::tryGetCurrentTrack';

    //me.logger.warn(me, log_label);
    me.logger.warn(me, log_label, {
      downloadTry: me.downloadTry,
      maxDownloadTry: me.maxDownloadTry
    });

    me.getCurrentTrack().then((result) => {
      me.logger.warn(me, log_label + ':success');

      /* https://github.com/apache/cordova-plugin-file */
      /*console.log("localURL", result.file.localURL);
      console.log("toInternalURL", result.entry.toInternalURL());
      console.log("toURL", result.entry.toURL());*/
      me.playTrack(result.file.localURL);
      //this.syncService.playTrack(result.entry.toInternalURL());
      //this.syncService.playTrack(result.entry.fullPath);
      //this.syncService.playTrack(result.entry.name);
      //this.syncService.playTrack("cdvfile://localhost/persistent" + result.entry.fullPath);

      me.dismissLoading();

    }, error => {
      me.logger.error(me, log_label + ':error', {error: error});

      me.downloadTry++;

      if (me.downloadTry < me.maxDownloadTry) {
        // retry with delay
        let timeout: Number = 1000 * me.downloadTry * me.downloadInterval;
        me.logger.warn(me, log_label + ':wait', {timeout: timeout});
        me.logger.info(me, log_label + ':wait', {timeout: timeout, channel: me.channel, current: me.current});

        setTimeout(() => {
          me.logger.info(me, log_label + ':wait:run', {timeout: timeout, channel: me.channel, current: me.current});

          me.tryGetCurrentTrack();
        }, timeout);

      } else {
        // max tries reached
        me.logger.info(me, log_label + ':wait:maxed');

        me.dismissLoading().then( () => {
          me.markTrackDefect(me.current, error).then(() => {
            me.logger.info(me, log_label + ':wait:maxed:markTrackDefect');

            // first try: period around the scheduled track from the fallback playlist
            me.getFallbackTrackByPeriodOfTime().then(() => {

              me.downloadTry = 0;
              me.tryGetCurrentTrack();

            }, error => {
              me.logger.error(me, log_label + ':wait:first:getFallbackTrackByPeriodOfTime:error', {
                error: error
              });

              // second try: random track from the fallback playlist
              me.getFallbackTrackByPeriodOfTime(true).then(() => {

                me.downloadTry = 0;
                me.tryGetCurrentTrack();

              }, error => {
                me.logger.error(me, log_label + ':wait:second:getFallbackTrackByPeriodOfTime:error', {
                  error: error
                });

                // third try: random track from all files
                me.getRandomTrack().then(() => {

                  me.downloadTry = 0;
                  me.tryGetCurrentTrack();

                }, error => {
                  me.logger.error(me, log_label + ':wait:third:getRandomTrack:error', {
                    error: error
                  });
                });
              });
            });
          });
        });
      }
    });
  }

  getFallbackTrackByPeriodOfTime(allDay: boolean = false): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getFallbackTrackByPeriodOfTime';

    me.logger.error(me, log_label);

    let fallbacks_label: string = me.getFallbacksLabel();
    let fallback_label: string  = fallbacks_label + "::pl_fallback_" + me.current.weekday;

    return new Promise((resolve, reject) => {

      me.logger.info(me, log_label + ":fallbacks", {fallbacks_type: typeof me.fallbacks[me.current.channel_id]});
      if (typeof me.fallbacks[me.current.channel_id] == "undefined") {
        reject("fallbacks not ready yet");
      }

      me.logger.debug(me, log_label, {
        last_track: me.last,
        current_track: me.current,
        current_playlist: me.playlist,
        fallback_label: fallback_label,
        fallbacks: Object.keys(me.fallbacks[me.current.channel_id]),
        allDay: allDay
      });

      me.storage.get(fallback_label).then((fallback_playlist) => {
        me.logger.debug(me, log_label + ':get', {
          fallback_playlist: fallback_playlist
        });

        if (!fallback_playlist || !fallback_playlist[0]) {
          // 2018-04-09 TODO
          let msg: string = 'Fallback playlist ' + fallback_label + ' missing!';

          me.logger.error(me, log_label + ':get:missing', {
            error: msg
          });

          reject(msg);
        }

        // adjust date to fallback list
        let current: any  = moment(me.current.starttime.gmt);
        let st: any       = moment(fallback_playlist[0].starttime.gmt);

        let nst: any = st.clone();
        nst = nst.set({
          'hour':   current.get('hour'),
          'minute': current.get('minute'),
          'second': current.get('second')
        });

        // define time period
        let timeRadius: number = 15; // in minutes
        let start: number = nst.unix() - timeRadius * 60;
        let end: number = nst.unix() + timeRadius * 60;
        if (allDay) {
          start = nst.startOf('day').unix();
          end = nst.endOf('day').unix();
        }
        me.logger.debug(me, log_label + ':times', {
          start: start,
          end: end,
          allDay: allDay
        });

        // skipable track_ids
        let skipable_track_ids: Array<any> = [];
        skipable_track_ids.push(me.current.track_id);
        if (!isNullOrEmpty.isNullOrEmpty(me.last)) {
          skipable_track_ids.push(me.last.track_id);
        }
        me.logger.debug(me, log_label + ':skipable_track_ids', {skipable_track_ids: skipable_track_ids});

        // fill pool
        let fallbackPool: Array<any> = [];

        fallback_playlist.forEach((fallback_track) => {
          //if (debug) console.log(log_label + ":foreach", start, fallback_track.starttime.unix, end);

          if (
            !skipable_track_ids.includes(fallback_track.track_id)
            &&
            (
              fallback_track.starttime.unix >= start
              &&
              fallback_track.starttime.unix <= end
            )
          ) {
            fallbackPool.push(fallback_track);
          }
        });

        me.logger.debug(me, log_label + ':fallbackPool', {fallbackPool: fallbackPool});

        if (fallbackPool.length == 0) {
          let error = new Error("Empty fallback pool");
          reject(error);
        }

        // random element
        fallbackPool.sort(function() {return 0.5 - Math.random()});
        me.current = fallbackPool[0];
        me.current.duration = me.current.playtime;
        // 2019-03-05
        me.current.played = 0;

        me.logger.debug(me, log_label + ':current', {current: me.current});

        if (me.globalvarsProvider.devMode && me.globalvarsProvider.trackMissingTest) {
          me.globalvarsProvider.trackMissingTest = false;
        }

        resolve();

      }, error => {
        me.logger.error(me, log_label + ':get:error', {
          error: error
        });

        reject(error);
      })
    });
  }

  getRandomTrack(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::getRandomTrack';

    me.logger.error(me, log_label, {current: me.current});

    let catalog: any = {};

    return new Promise((resolve, reject) => {

      let storage_label: string = me.getTracksLabel(me.current.channel_id);

      me.storage.get(storage_label).then(tracks => {
        me.logger.warn(me, log_label + ":tracks", {storage_label: storage_label, tracks: tracks});

        if (isNullOrEmpty.isNullOrEmpty(tracks)) {
          reject("No tracks downloaded: " + storage_label);

        } else {
          let trackPaths: Array<string> = [];
          Object.keys(tracks).forEach(track_id => {
            trackPaths.push(tracks[track_id].entry.nativeURL);
            catalog[tracks[track_id].entry.nativeURL] = tracks[track_id];
          });
          me.logger.warn(me, log_label + ':trackPaths', {trackPaths: trackPaths});

          me.getStorageDirectory(me.channel).then((dir) => {
            me.logger.debug(me, log_label + ':getStorageDirectory', {dir: dir});

            me.listDir(dir.dir).then(listDir => {
              me.logger.debug(me, log_label + ':listDir', {listDir: listDir});

              if (isNullOrEmpty.isNullOrEmpty(listDir)) {
                // empty => nothing to do
                reject("No tracks downloaded: " + storage_label);

              } else {
                // files => compare
                let storagePaths: Array<string> = [];
                listDir.forEach(listDirEntry => {
                  storagePaths.push(listDirEntry.nativeURL);
                });
                me.logger.warn(me, log_label + ':storagePaths', {storagePaths: storagePaths});

                // exists
                let exists = storagePaths.filter(x => trackPaths.includes(x));
                me.logger.warn(me, log_label + ':exists', {exists: exists, catalog: catalog});

                if (exists.length == 0) {
                  // no needless tracks
                  reject("No tracks downloaded: " + storage_label);

                } else {
                  // select random
                  let key: string = exists[Math.floor(Math.random()*exists.length)];

                  let playlists_label: string = me.getPlaylistsLabel(me.channel);

                  me.storage.get(playlists_label).then(playlists => {
                    me.logger.warn(me, log_label + ":playlists", {playlists: playlists});

                    me.findUniqueTracks(me.channel, playlists, playlists_label).then(() => {
                      me.logger.warn(me, log_label + ":uniqueTracks", {key: key, catalog: catalog[key], uniqueTracks: me.uniqueTracks[me.channel.channel_id]});

                      if (isNullOrEmpty.isNullOrEmpty(me.uniqueTracks[me.channel.channel_id])) {
                        reject("No tracks downloaded: " + storage_label);

                      } else {
                        let size: number = catalog[key].data.size;

                        let trackname: string = key.split(/\//).pop();
                        me.logger.warn(me, log_label + ":trackname", {trackname: trackname});
                        trackname = trackname.split(/\_/, 2).pop();

                        me.logger.warn(me, log_label + ":info", {size: size, trackname: trackname});

                        Object.keys(me.uniqueTracks[me.channel.channel_id]).forEach(track_id => {
                          let track: any = me.uniqueTracks[me.channel.channel_id][track_id];

                          if (track.track_path.includes(trackname) && track.file_size == size) {
                            me.current = track;
                            me.current.duration = me.current.playtime;
                            // 2019-03-05
                            me.current.played = 0;

                            me.logger.debug(me, log_label + ':current', {current: me.current});

                            resolve();
                          }
                        });
                      }

                    }, error => {
                      reject(error);
                    });

                  }, error => {
                    reject(error);
                  });
                }
              }

            }, error => {
              reject(error);
            });

          }, error => {
            reject(error);
          });

        }

      }, error => {
        reject(error);
      });
    });
  }

  setInterrupt(interrupt: boolean): void {
    const me = this;
    let log_label = me.log_label_main + '::setInterrupt';

    me.logger.warn(me, log_label);

    me.interrupt = interrupt;
  }

  doPlay(): void {
    const me = this;
    let log_label = me.log_label_main + '::doPlay';

    me.logger.warn(me, log_label);

    //me.mediaObject.play();
    me.initiatePlaylist();
  }

  doPause(): void {
    const me = this;
    let log_label = me.log_label_main + '::doPause';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {mediaObject_type: typeof me.mediaObject, mediaObject: me.mediaObject});

    if (me.mediaObject) {
      me.mediaObject.pause();
    }
  }

  changeVolume(volume: number): void {
    const me = this;
    let log_label = me.log_label_main + '::changeVolume';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {volume: volume, lastVolume: me.lastVolume});

    me.lastVolume = volume;
    if (!isNullOrEmpty(me.mediaObject)) {
      me.mediaObject.setVolume(volume);
    }

    me.storage.set('last_volume', volume).then(() => {
      me.logger.info(me, log_label + ":set", {volume: volume, lastVolume: me.lastVolume});

    }, error => {
      me.logger.error(me, log_label + ":error", {error: error});
    });
  }

  resetMusicControls(destroy: boolean = false): void {
    const me = this;
    let log_label = me.log_label_main + '::resetMusicControls';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      musicControlPaused: me.musicControlPaused,
      current_type: typeof me.current,
      current: me.current,
      musicControls: me.musicControls,
      destroy: destroy
    });

    // SIA-18, 2019-01-29, no destruction needed, just recreation
    // 2019-02-12, added check
    if (destroy) me.musicControls.destroy();
    me.musicControl = null;
  }

  private createMusicControls(): void {
    const me = this;
    let log_label = me.log_label_main + '::createMusicControls';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      musicControlPaused: me.musicControlPaused,
      current_type: typeof me.current,
      current: me.current,
      musicControls: me.musicControls
    });

    // 2019-01-04 TODO: platform selection
    let check: boolean = false;
    // 2019-01-31, TODO: android testing
    //check = me.platform.is("android");
    if (check) {
      me.logger.info(me, log_label + ":platform", {platform: me.platform});

    } else {
      me.logger.info(me, log_label + ":control", {
        current: me.current,
        channel: me.channel,
        musicControl: me.musicControl,
        musicControlPaused: me.musicControlPaused,
        musicControlLastTrack: me.musicControlLastTrack
      });
      me.logger.warn(me, log_label + ":status", {
        current: me.current,
        musicControlPaused: me.musicControlPaused,
        musicControlLastTrack: me.musicControlLastTrack
      });

      let duration: number = Math.round(me.current.duration);
      let elapsed: number = Math.round(me.current.played);

      me.logger.info(me, log_label + ":control:times", {elapsed: elapsed, duration: duration});

      // destruction needed for resetting track, artist and album info
      // SIA-18, 2019-01-29, added track change

      let status: string = "create";
      if (me.musicControl) {
        if (!me.musicControlPaused) {
          // paused
          status = "recreate";

        } else if (
          me.musicControlLastTrack
          &&
          me.current
          &&
          me.current.id != me.musicControlLastTrack.id
        ) {
          // track change
          status = "recreate";

        } else {
          // update
          status = "update";
        }
      }
      me.logger.warn(me, log_label, status);

      if (status == "update") {
        // update

        //me.musicControls.updateIsPlaying(true);
        me.musicControls.updateElapsed({isPlaying: true, elapsed: elapsed.toString(10)});
        //me.musicControls.updateDismissable(true);

        me.musicControlPaused = false;

      } else {
        // create or recreate

        let mcInfo: MusicControllerInfo = {
          track: '',
          artist: '',
          album: '',
          elapsed: 0,
          duration: 0,
          ticker: ''
        };

        if (me.current) {
          if (!isNullOrEmpty(me.current.title)) mcInfo.track = me.current.title;
          if (!isNullOrEmpty(me.current.artist)) mcInfo.artist = me.current.artist;

          if (
            !isNullOrEmpty(me.current.title)
            &&
            !isNullOrEmpty(me.current.artist)
          ) {
            mcInfo.ticker = me.current.artist + " - " + me.current.title;
          }
        }

        if (me.globalvarsProvider.selectedChannel) {
          if (!isNullOrEmpty(me.globalvarsProvider.selectedChannel.name)) mcInfo.album = me.globalvarsProvider.selectedChannel.name;
        }

        if (!isNaN(duration)) {
          if (duration > 0) mcInfo.duration = duration;
        }

        if (!isNaN(elapsed)) {
          if (elapsed > 0) mcInfo.elapsed = elapsed;
        }

        me.logger.warn(me, log_label + ":control:mcInfo", {mcInfo: mcInfo});

        try {
          me.musicControl = me.musicControls.create({
            track: mcInfo.track,                // optional, default : ''
            artist: mcInfo.artist,              // optional, default : ''
            cover: 'assets/icon.png',           // optional, default : nothing
            // cover can be a local path (use fullpath 'file:///storage/emulated/...', or only 'my_image.jpg' if my_image.jpg is in the www folder of your app)
            //           or a remote url ('http://...', 'https://...', 'ftp://...')
            isPlaying: true,                    // optional, default : true
            dismissable: true,                  // optional, default : false

            // hide previous/next/close buttons:
            hasPrev: false,                     // show previous button, optional, default: true
            hasNext: false,                     // show next button, optional, default: true
            hasClose: false,                    // show close button, optional, default: false

            // iOS only, optional
            album: mcInfo.album,                // optional, default: ''
            duration: mcInfo.duration,          // optional, default: 0
            elapsed: mcInfo.elapsed,            // optional, default: 0
            hasSkipForward: false,              // show skip forward button, optional, default: false
            hasSkipBackward: false,             // show skip backward button, optional, default: false
            skipForwardInterval: 0,             // display number for skip forward, optional, default: 0
            skipBackwardInterval: 0,            // display number for skip backward, optional, default: 0
            hasScrubbing: false,                // enable scrubbing from control center and lockscreen progress bar, optional

            // Android only, optional
            // text displayed in the status bar when the notification (and the ticker) are updated
            ticker: mcInfo.ticker,

            // All icons default to their built-in android equivalents
            // The supplied drawable name, e.g. 'media_play', is the name of a drawable found under android/res/drawable* folders
            playIcon: 'media_play',
            pauseIcon: 'media_pause',
            prevIcon: 'media_prev',
            nextIcon: 'media_next',
            closeIcon: 'media_close',
            notificationIcon: 'notification'

          })

          // 2019-03-17, https://github.com/homerours/cordova-music-controls-plugin/issues/94

            .catch((error) => {
              me.logger.error(me, log_label + ':create:error', {
                error: error,
                mcInfo: mcInfo
              });
            })

            .then(() => {
              me.logger.warn(me, log_label + ':create:created');

              // 2019-07-30 moved, promise not firing anymore (iOS)
              // https://github.com/homerours/cordova-music-controls-plugin/issues/110
              if (me.platform.is('android')) {
                me.createControlListeners(status);
              }
            });

        } catch (e) {
          me.logger.error(me, log_label + ":control:error", {e: e, mcInfo: mcInfo});
        }

        // 2019-08-08 alternative to iOS failing promise
        if (me.platform.is('ios')) {
          me.createControlListeners(status);
        }
      }
    }
  }

  private createControlListeners(status: string): void {
    const me = this;
    let log_label = me.log_label_main + '::createControlListeners';

    me.logger.warn(me, log_label);

    // SIA-18, 2019-01-29
    //if (status == "create") {
    me.musicControls.subscribe().subscribe((action) => {
      me.logger.info(me, log_label, {action: action});

      const message = JSON.parse(action).message;
      me.logger.info(me, log_label, {event: message});

      let elapsed: number = Math.round(me.current.played);
      me.logger.info(me, log_label, {elapsed: elapsed, elapsed_string: elapsed.toString(10)});

      switch (message) {
        case 'music-controls-next':
          // Do something
          break;
        case 'music-controls-previous':
          // Do something
          break;

        case 'music-controls-pause':
          // Do something
          me.doPause();

          // 2019-01-10
          me.musicControls.updateElapsed({isPlaying: false, elapsed: elapsed.toString(10)});
          // 2019-01-31
          if (me.platform.is("android")) {
            me.musicControls.updateIsPlaying(false);
          }
          //me.musicControls.updateDismissable(true);
          me.musicControlPaused = true;
          break;

        case 'music-controls-play':
          // Do something
          me.doPlay();

          // 2019-01-10
          //me.musicControls.updateElapsed({isPlaying: true, elapsed: elapsed.toString(10)});
          // 2019-01-31
          if (me.platform.is("android")) {
            me.musicControls.updateIsPlaying(true);
          }
          //me.musicControls.updateDismissable(true);
          break;

        case 'music-controls-destroy':
          // Do something
          me.doPause();

          // 2019-01-10
          me.musicControls.destroy();
          me.musicControl = null;
          break;

        // External controls (iOS only)
        case 'music-controls-toggle-play-pause' :
          // Do something
          break;
        case 'music-controls-seek-to':
          //const seekToInSeconds = JSON.parse(action).position;
          /*MusicControls.updateElapsed({
            elapsed: seekToInSeconds,
            isPlaying: true
          });*/
          // Do something
          break;
        case 'music-controls-skip-forward':
          // Do something
          break;
        case 'music-controls-skip-backward':
          // Do something
          break;

        // Headset events (Android only)
        // All media button events are listed below
        case 'music-controls-media-button' :
          // Do something
          break;
        case 'music-controls-headset-unplugged':
          // Do something
          break;
        case 'music-controls-headset-plugged':
          // Do something
          break;
        default:
          break;
      }
    });

    me.musicControls.listen(); // activates the observable above
    //}

    me.musicControls.updateIsPlaying(true);

    // SIA-18, 2019-01-29
    me.musicControlLastTrack = me.current;
  }

  deletePlaylists(channel: Channel): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::deletePlaylists';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {channel_type: typeof channel, channel: channel});

    let playlists_label = "playlists_" + channel.channel_id + "::";

    return new Promise((resolve, reject) => {
      let promises = [];

      me.storage.keys().then(keys => {
        me.logger.debug(me, log_label, {keys: keys});

        keys.forEach(key => {
          if (key.includes(playlists_label)) {
            me.logger.info(me, log_label, {key: key});
            promises.push(me.storage.remove(key));
          }
        });

        Promise.all(promises).then(() => {
          me.setPlaylistInfo(channel, true, false);

          resolve();

        }, error => {
          reject(error);
        });
      });
    });
  }

  deleteFallbacks(channel: Channel): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::deleteFallbacks';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {channel_type: typeof channel, channel: channel});

    let fallbacks_label = "fallbacks_" + channel.channel_id + "::";

    return new Promise((resolve, reject) => {
      let promises = [];

      me.storage.keys().then(keys => {
        me.logger.debug(me, log_label, {keys: keys});

        keys.forEach(key => {
          if (key.includes(fallbacks_label)) {
            me.logger.info(me, log_label, {key: key});
            promises.push(me.storage.remove(key));
          }
        });

        Promise.all(promises).then(() => {
          me.setPlaylistInfo(channel, false, true);

          resolve();

        }, error => {
          reject(error);
        });
      });
    });
  }

  deleteStoredTrack(channel: any, track: any): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::deleteStoredTrack';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {channel_type: typeof channel, channel: channel, track_type: typeof track, track: track});

    let tracks_label: string = "tracks_" + channel.channel_id;

    return new Promise((resolve, reject) => {
      me.storage.get(tracks_label).then(tracks => {
        me.logger.debug(me, log_label, {tracks: tracks});

        if (isNullOrEmpty.isNullOrEmpty(tracks)) {
          reject();
        }

        if (typeof track == "object") {
          if (track.track_id) {
            me.logger.info(me, log_label + ":track", {track: tracks[track.track_id]});
            delete tracks[track.track_id];

          } else if (track.length > 0) {
            me.logger.info(me, log_label + ":tracks", {tracks: track});
            track.forEach((track_id) => {
              delete tracks[track_id];
            })
          }
        }

        me.storage.set(tracks_label, tracks).then(() => {
          resolve();

        }, error => {
          reject(error);
        })

      }, error => {
        reject(error);
      });
    });
  }

  deleteTracks(channel: Channel): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::deleteTracks';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label, {
      channel: me.channel
    });

    let tracks_label: string = "tracks_" + channel.channel_id;

    return new Promise((resolve, reject) => {
      let promises: any = [];

      promises.push(me.file.removeRecursively(me.storageDirectory, "ch_" + channel.channel_id));
      promises.push(me.storage.remove(tracks_label));

      Promise.all(promises).then(() => {
        me.logger.warn(me, log_label + ":complete");

        me.getStorageDirectory(channel).then(dir => {

          me.setTrackInfo(channel);

          me.getFreeSpace().then(() => {
            resolve();

          }, error => {
            reject(error);
          })

        }, error => {
          reject(error);
        });

      }, error => {
        reject(error);
      });
    });
  }

  exportAll(): void {
    const me = this;
    let log_label = me.log_label_main + '::exportAll';

    me.logger.warn(me, log_label);

    let filesystem: any = {};
    let storage: any = {};

    let promises: any = [];

    // storage
    let deferredStorage: Deferred<any> = new Deferred<any>();
    promises.push(deferredStorage.promise);

    let promisesStorage: any = [];

    me.storage.keys().then(keys => {
      me.logger.warn(me, log_label + ':keys', {keys: keys});

      keys.forEach((key, idx) => {
        promisesStorage.push(new Promise(resolve => {
          me.storage.get(key).then((result) => {
            me.logger.warn(me, log_label + ':key', {key: key, result: result});

            storage[key] = result;

            resolve();

          }, error => {
            me.logger.error(me, log_label + ':key:error', {key: key, error: error});
          });

        }));
      });

      Promise.all(promisesStorage).then(() => {
        me.logger.warn(me, log_label + ':storage:done');

        deferredStorage.resolve();
      });
    });

    // files
    let deferredFiles: Deferred<any> = new Deferred<any>();
    promises.push(deferredFiles.promise);

    let promisesFiles: any = [];

    me.file.listDir(me.storageDirectory, ".").then(listDir => {
      me.logger.warn(me, log_label + ':listDir', {listDir: listDir});

      listDir.forEach((dir, idx) => {
        me.logger.info(me, log_label + ':listDir:dir', {dir: dir});

        filesystem[dir.name] = [];

        promisesFiles.push(new Promise(resolve => {
          me.file.listDir(me.storageDirectory, dir.name).then(fileDir => {
            me.logger.warn(me, log_label + ':fileDir', {fileDir: fileDir});

            //me.logger.warn(me, log_label + ':fileDir:dir', {dir: fileDir});
            fileDir.forEach((file) => {
              filesystem[dir.name].push(file.nativeURL);
            });

            resolve();

          }, error => {
            me.logger.error(me, log_label + ':fileDir:error', {error: error});
          });
        }));
      });

      Promise.all(promisesFiles).then(() => {
        me.logger.warn(me, log_label + ':files:done');

        deferredFiles.resolve();
      });
    });

    // all
    Promise.all(promises).then(() => {
      me.logger.warn(me, log_label + ':done');
      me.logger.warn(me, log_label + ':done:data', {
        filesystem: filesystem,
        storage: storage,
        defectTracks: me.defectTracks
      });

      let filename: string = "export.json";
      let url: string = me.storageDirectory;

      let json: string = JSON.stringify({storage: storage, filesystem: filesystem, defectTracks: me.defectTracks});

      me.file.writeFile(url, filename, json, {replace: true}).then(result => {
        me.logger.warn(me, log_label + ':write', {url: url, filename: filename, result: result});

        me.fileOpener.open(result.nativeURL, 'text/plain') // 'application/json'
          .then(() => { me.logger.warn(me, log_label + ':opening')})
          .catch(e => { me.logger.error(me, log_label + ':opening:error', e)});
      });
    });
  }

  deleteAll(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::deleteAll';

    me.logger.warn(me, log_label);

    return new Promise((resolve, reject) => {
      let promises: any = [];

      me.doPause();

      me.stopAutoSyncs();

      /**/
      promises.push(me.file.listDir(me.storageDirectory, ".").then(listDir => {
        me.logger.info(me, log_label + ':listDir', {listDir: listDir});

        if (listDir) {
          listDir.forEach((dir) => {
            me.logger.debug(me, log_label + ':listDir:dir', {dir: dir});

            promises.push(
              me.file.removeRecursively(me.storageDirectory, dir.name)
                .then(() => {})
                .catch(() => {})
            );
          });
        }
      }));
      /**/

      /**/
      promises.push(me.storage.keys().then(keys => {
        me.logger.info(me, log_label + ':keys', {keys: keys});

        if (keys) {
          keys.forEach((key) => {
            me.logger.debug(me, log_label + ':keys:key', {key: key});

            if (
              key.includes("playlists_")
              ||
              key.includes("fallbacks_")
              ||
              key.includes("tracks_")
            ) {
              me.logger.debug(me, log_label + ':keys:key', {key: key});

              promises.push(
                me.storage.remove(key)
                  .then(() => {})
                  .catch(() => {})
              );
            }

          });
        }
      }));
      /**/

      Promise.all(promises).then(() => {
        me.logger.warn(me, log_label + ':done');

        me.getFreeSpace();

        me.channels.forEach((channel: Channel) => {
          me.setPlaylistInfo(channel);
          me.setTrackInfo(channel);
        });

        // 2019-03-22
        me.firstPlay = false;

        resolve();

      }, (error) => {
        me.logger.error(me, log_label + ':done:error', {error: error});

        reject(error);
      });

    });
  }

  dismissLoading(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::dismissLoading';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label + ":loading", {
      loading: me.loading,
      notLoading: !me.loading,
      dismissing: me.loadingDismussing
    });

    return new Promise((resolve) => {
      if (me.loadingDismussing) {
        me.logger.info(me, log_label + ":loading:dismissing");

        me.logger.debug(me, log_label + ":loading:resolve");
        resolve();

      } else {
        if (!me.loading) {
          me.logger.info(me, log_label + ":loading:none");

          me.logger.debug(me, log_label + ":loading:resolve");
          resolve();

        } else {
          me.loadingDismussing = true;

          try {
            me.loading.dismiss().then(() => {
              me.logger.info(me, log_label + ":loading:done");

              me.clearDismissing();

              me.logger.debug(me, log_label + ":loading:resolve");
              resolve();

            }, error => {
              me.logger.error(me, log_label + ":loading:error", {error: error});

              me.clearDismissing();

              me.logger.debug(me, log_label + ":loading:resolve");
              resolve();
            });

          } catch (e) {
            me.logger.error(me, log_label + ":loading:e", {e: e});

            me.clearDismissing();

            me.logger.debug(me, log_label + ":loading:resolve");
            resolve();
          }
        }
      }
    });
  }

  private clearDismissing(): void {
    const me = this;
    let log_label = me.log_label_main + '::clearDismissing';

    me.logger.warn(me, log_label);

    // 2018-04-10 TODO: unresolving dismissLoading
    me.loading = null;
    me.loadingDismussing = false;
  }

  showLoading(message: string): void {
    const me = this;
    let log_label = me.log_label_main + '::showLoading';

    me.logger.warn(me, log_label);
    me.logger.info(me, log_label + ":loading", {
      loading: me.loading,
      notLoading: !me.loading
    });

    if (!me.loading) {
      me.loading = me.loadingCtrl.create({
        content: message,
        dismissOnPageChange: false/*,
           duration: 3000,*/
      });
      me.loading.present();
    }
  }

  private loadVolume(): Promise<any> {
    const me = this;
    let log_label = me.log_label_main + '::loadVolume';

    me.logger.warn(me, log_label);

    return new Promise((resolve, reject) => {
      let storageLabel: string = 'last_volume';

      me.storage.get(storageLabel).then((volume) => {
        me.logger.info(me, log_label + ":storage:get", {storageLabel: storageLabel, volume: volume});

        me.lastVolume = volume;

        me.logger.info(me, log_label + ':resolve');
        resolve();

      }, error => {
        me.logger.error(me, log_label + ":storage:get:error", {error: error});
        reject(error);
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/sync/sync.ts